import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Paper,
  Toolbar,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  LinearProgress,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  ListSubheader,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Autocomplete,
  FormHelperText,
  FormControlLabel,
  Switch,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  FormatAlignLeft as FormatIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,

  Router as RouterIcon,
  Light as LightIcon,
  Videocam as VideocamIcon,
  Link as LinkIcon,
  Security as AccessListIcon,
  Tune as ConfigureIcon,
  Map as MapIcon,
  Send as CommandIcon,
  Monitor as RttyIcon,
  Download as DownloadIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import {
  getParentLatencyMs,
  getCapLatencyMs,
  getLatencyValue,
  getLatencyChipColor,
  getLatencyLabel,
  formatLatencyMs,
  formatLatencyTooltip,
} from '../services/latency';
import { auth } from '../services/auth';
import config from '../config';
import Editor from '@monaco-editor/react';
import LastKnownGoodMenu from './LastKnownGoodMenu';
import { isLastKnownGoodEligible } from '../utils/lkgUtils';
import PortForwardingSection from './PortForwardingSection';
import FirewallSection from './FirewallSection';
import SyslogSection from './SyslogSection';
import ReportsSection from './ReportsSection';
import DeviceReportMenuButton from './DeviceReportMenuButton';
import DevicePublicWifiConfigPanel from './DevicePublicWifiConfigPanel';
import { fieldSectionSx } from './deviceConfigStyles';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import DeviceNotesEditor from './DeviceNotesEditor';
import CommandHistory from './CommandHistory';
import { RadioApSegments } from './ApFailsafeButton';
import RebootAiBoardButton from './RebootAiBoardButton';
import {
  deviceAiBoardMac,
  deviceHasNotes,
  deviceSupportsApFailsafe,
  FEFE_SEGMENT_ID,
  LEGACY_WILDCARD_SEGMENT_ID,
  parseSegmentIdToken,
  sameSegmentIdList,
  segmentIdLabel,
  treeIdForApRadio,
} from '../utils/deviceLampActions';
import { MAP_UI } from '../theme/mapViewUi';
import {
  bytesToMB,
  computeDeviceDataCounters,
  formatRateMbps,
  getDataCounterRates,
  getTrafficRate,
  hasDataCounterRates,
  isCapDevice,
} from '../utils/deviceDataCounters';

// =============================================================================
// Channel-group ID catalogue (treemgmt PATCH/POST)
// =============================================================================
//
// `channel_group_ids` on a treemgmt entry is a list of pre-defined regulatory
// U-NII sub-bands. The backend expands the IDs into the equivalent
// `channel_groups` map per band at configure time, and resets the fixed
// `channel` to 0 (auto). DFS sub-bands (U-NII-2, U-NII-4) are intentionally
// not exposed.
//
// Keep this list in sync with openapi.yaml's PATCH/POST /tree-mgmt/* docs.
const UNII_GROUPS = [
  { id: 1, label: 'U-NII-1',  band: '5GHz', channels: '36 – 48' },
  { id: 2, label: 'U-NII-3',  band: '5GHz', channels: '149 – 165' },
  { id: 3, label: 'U-NII-5',  band: '6GHz', channels: '1 – 93' },
  { id: 4, label: 'U-NII-6',  band: '6GHz', channels: '97 – 113' },
  { id: 5, label: 'U-NII-7',  band: '6GHz', channels: '117 – 185' },
  { id: 6, label: 'U-NII-8',  band: '6GHz', channels: '189 – 221' },
];
const UNII_BY_ID = UNII_GROUPS.reduce((acc, g) => { acc[g.id] = g; return acc; }, {});

// Returns "1 — U-NII-1 (5 GHz, ch 36 – 48)" for a known id; falls back to the
// raw id for unknown values.
const formatUniiOption = (g) => `${g.id} — ${g.label} (${g.band === '5GHz' ? '5 GHz' : '6 GHz'}, ch ${g.channels})`;

// Builds a one-line per-band summary, e.g.:
//   "Auto channel pick across 5 GHz: ch 36 – 48; 6 GHz: ch 1 – 93, 97 – 113"
const summarizeUniiSelection = (ids) => {
  if (!Array.isArray(ids) || ids.length === 0) return '';
  const buckets = { '5GHz': [], '6GHz': [] };
  ids.forEach((id) => {
    const g = UNII_BY_ID[id];
    if (g) buckets[g.band].push(g.channels);
  });
  const parts = [];
  if (buckets['5GHz'].length) parts.push(`5 GHz: ch ${buckets['5GHz'].join(', ')}`);
  if (buckets['6GHz'].length) parts.push(`6 GHz: ch ${buckets['6GHz'].join(', ')}`);
  return parts.length ? `Auto channel pick across ${parts.join('; ')}` : '';
};

// Unused VLAN sub-interfaces (e.g. ath1.14) report mode ""; hide from Radio Interfaces UI.
const filterRadioInterfacesByMode = (interfaces) => {
  if (!interfaces || !Array.isArray(interfaces)) return [];
  return interfaces.filter((iface) => {
    const mode = iface?.Mode ?? iface?.mode;
    return String(mode ?? '').trim() !== '';
  });
};

const normalizeStationKey = (raw) => {
  if (raw == null || raw === '') return '';
  return String(raw).replace(/:/g, '').toLowerCase();
};

const isAPModeIface = (iface) => {
  const mode = String(iface?.Mode ?? iface?.mode ?? 'ap').toLowerCase();
  return mode === 'ap';
};

const deviceAssociationMatchKeys = (d) => {
  const keys = [];
  if (d?.serial_number) {
    const serial = d.serial_number.toLowerCase();
    keys.push(serial, serial.replace(/:/g, ''));
  }
  (d?.wifi_interfaces || []).forEach((w) => {
    const mac = normalizeStationKey(w?.macaddr ?? w?.mac);
    if (mac) keys.push(mac);
  });
  (d?.radios || []).forEach((r) => {
    const mac = normalizeStationKey(r?.mac);
    if (mac) keys.push(mac);
  });
  const privIfaces = d?.private_wifi?.interfaces || d?.private_wifi?.Interfaces || [];
  const stationIface = privIfaces.find(
    (iface) => String(iface?.Mode ?? iface?.mode ?? '').toLowerCase() === 'station',
  );
  const stationMac = normalizeStationKey(stationIface?.MAC ?? stationIface?.mac);
  if (stationMac) keys.push(stationMac);
  return keys;
};

const countActiveAssociations = (ifaces) => (ifaces || [])
  .filter(isAPModeIface)
  .reduce((total, iface) => total + (iface.Associations || iface.associations || []).length, 0);

const renderDataCounterTraffic = (counters, rates, rateKey) => {
  const rate = getTrafficRate(rates, rateKey);
  const showRates = hasDataCounterRates(rates);
  return (
    <>
      <Typography variant="body2">
        <strong>RX:</strong> {bytesToMB(counters?.rx_bytes)} MB
        {showRates && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            ({formatRateMbps(rate.rx_bytes_per_sec)} Mb/s)
          </Typography>
        )}
      </Typography>
      <Typography variant="body2">
        <strong>TX:</strong> {bytesToMB(counters?.tx_bytes)} MB
        {showRates && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            ({formatRateMbps(rate.tx_bytes_per_sec)} Mb/s)
          </Typography>
        )}
      </Typography>
    </>
  );
};

// Public WiFi client count from GET /device. -1 means that band is inactive for this interface.
const publicWifiClientCountForInterface = (iface, publicWifi, band) => {
  const count24 = iface?.num_clients_24g ?? publicWifi?.num_clients_24g;
  const count5 = iface?.num_clients_5g ?? publicWifi?.num_clients_5g;
  const active24 = count24 != null && count24 !== -1;
  const active5 = count5 != null && count5 !== -1;

  if (active24 && !active5) return Math.max(0, count24);
  if (active5 && !active24) return Math.max(0, count5);
  if (!active24 && !active5) return 0;

  const bandLower = String(band || '').toLowerCase();
  if (bandLower.includes('2.4')) return Math.max(0, count24);
  if (bandLower.includes('5') || bandLower.includes('6')) return Math.max(0, count5);
  return Math.max(0, count24, count5);
};

const BG_SELF_IMPROVEMENT_ALWAYS = 2147483647;

const isBgSelfImprovementAlways = (value) => {
  if (value === '' || value === undefined || value === null) return false;
  if (String(value).toLowerCase() === 'always') return true;
  return Number(value) === BG_SELF_IMPROVEMENT_ALWAYS;
};

const formatBgSelfImprovementDisplay = (value) => {
  if (value === '' || value === undefined || value === null) return '';
  if (isBgSelfImprovementAlways(value)) return 'Always';
  return typeof value === 'number' ? String(value) : value;
};

const normalizeBgSelfImprovementInput = (value) => {
  const trimmed = String(value).trim();
  if (trimmed.toLowerCase() === 'always') {
    return String(BG_SELF_IMPROVEMENT_ALWAYS);
  }
  return trimmed;
};

const FieldLabelTip = ({ title, children }) => (
  <Tooltip title={title} enterDelay={400}>
    <Box component="span" sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help' }}>
      {children}
    </Box>
  </Tooltip>
);

const bgScanSelectRenderValue = (selected) => selected || '\u00a0';

const STATION_SEGMENT_EDIT_KEY = '*segment_dev';

const finiteSegmentIds = (ids) => (
  Array.isArray(ids) ? ids.map(Number).filter(Number.isFinite) : []
);

const infoSegmentChipLabel = (id) => (
  Number(id) === LEGACY_WILDCARD_SEGMENT_ID ? 'All' : segmentIdLabel(id)
);

const infoSegmentChipTitle = (id, segmentNames) => {
  const n = Number(id);
  if (n === FEFE_SEGMENT_ID) return '0xFEFE — this AP accepts children from any segment';
  if (n === LEGACY_WILDCARD_SEGMENT_ID) return '0xFFFF — all segments';
  const name = segmentNames?.get(n);
  return name ? `${name} (segment ${n})` : `Segment ${n}`;
};

const infoSegmentChip = (id, segmentNames, onOpen) => {
  const n = Number(id);
  const openable = n !== FEFE_SEGMENT_ID && n !== LEGACY_WILDCARD_SEGMENT_ID && typeof onOpen === 'function';
  const title = infoSegmentChipTitle(id, segmentNames);
  return (
    <Tooltip
      key={id}
      title={openable ? (
        <>
          {title}
          <br />
         Click to open in Manage Segments
        </>
      ) : title}
    >
      <Chip
        size="small"
        label={infoSegmentChipLabel(id)}
        clickable={openable}
        onClick={openable ? () => onOpen(n) : undefined}
        color={n === FEFE_SEGMENT_ID ? 'warning' : 'default'}
        variant={n === FEFE_SEGMENT_ID ? 'filled' : 'outlined'}
        sx={{
          height: 24,
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          '& .MuiChip-label': { px: 1 },
        }}
      />
    </Tooltip>
  );
};

/** Device Information block, laid out like Backhaul Health. Hidden when empty. */
const InfoSegmentChips = ({ device, segmentNames }) => {
  const navigate = useNavigate();
  const openManageSegment = (segmentId) => {
    const params = new URLSearchParams({
      viewMode: 'map',
      manageSegment: String(segmentId),
    });
    navigate(`/devices?${params.toString()}`);
  };
  const isCap = String(device?.capabilities?.model || device?.model || '').toLowerCase().includes('cap');
  const stationIds = isCap ? [] : finiteSegmentIds(device?.segment?.segment_id_dev);
  let apRows = (Array.isArray(device?.ap_segments) ? device.ap_segments : [])
    .map((row, index) => ({
      key: `ap-${row?.tree_id || index}`,
      label: 'AP:',
      treeId: row?.tree_id || '',
      ids: finiteSegmentIds(row?.segment_id_ap),
    }))
    .filter((row) => row.ids.length > 0);
  if (apRows.length === 0) {
    const fallback = finiteSegmentIds(device?.segment?.segment_id_ap);
    if (fallback.length > 0) apRows = [{ key: 'ap', label: 'AP:', treeId: '', ids: fallback }];
  }
  if (apRows.length > 1) {
    apRows = apRows.map((row) => ({
      ...row,
      label: row.treeId ? `AP ${row.treeId}:` : 'AP:',
    }));
  }
  if (stationIds.length === 0 && apRows.length === 0) return null;
  const apFromConfig = (Array.isArray(device?.ap_segments) ? device.ap_segments : [])
    .some((row) => finiteSegmentIds(row?.segment_id_ap).length > 0);
  const segmentsTitle = [
    stationIds.length > 0
      ? 'STA shows the segment IDs from the device’s last state report. Those IDs update when the device reports again.'
      : '',
    apRows.length === 0
      ? ''
      : (apFromConfig
        ? 'AP shows the segment IDs saved in the device configuration.'
        : 'AP shows the segment IDs from the device’s last state report.'),
  ].filter(Boolean).join(' ');
  const line = (key, label, ids) => (
    <Box key={key} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 1, ml: 2 }}>
      <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary', fontWeight: 'bold' }}>{label}</Typography>
      {ids.map((id) => infoSegmentChip(id, segmentNames, openManageSegment))}
    </Box>
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
        <FieldLabelTip title={segmentsTitle}>Segments:</FieldLabelTip>
      </Typography>
      {stationIds.length > 0 && line('sta', 'STA:', stationIds)}
      {apRows.map((row) => line(row.key, row.label, row.ids))}
    </Box>
  );
};

const SegmentIdListEditor = ({ label, helper, ids, disabled, onChange }) => {
  const [draft, setDraft] = useState('');
  const parsed = /^\d+$/.test(draft) ? parseSegmentIdToken(draft) : undefined;
  const canAdd = !disabled && parsed !== undefined;
  const add = () => {
    if (!canAdd) return;
    setDraft('');
    if (ids.includes(parsed)) return;
    onChange([...ids, parsed]);
  };
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.75 }}>{label}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, minHeight: 24 }}>
        {ids.length === 0 && (
          <Typography variant="caption" color="text.secondary">None</Typography>
        )}
        {ids.map((id) => (
          <Chip
            key={id}
            size="small"
            label={segmentIdLabel(id)}
            color={Number(id) === FEFE_SEGMENT_ID ? 'warning' : 'default'}
            variant={Number(id) === FEFE_SEGMENT_ID ? 'filled' : 'outlined'}
            onDelete={disabled ? undefined : () => onChange(ids.filter((existing) => existing !== id))}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          label="Add segment"
          value={draft}
          disabled={disabled}
          inputMode="numeric"
          onChange={(e) => {
            setDraft(e.target.value.replace(/\D/g, ''));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          error={draft !== '' && parsed === undefined}
          helperText={draft !== '' && parsed === undefined ? 'Enter a number from 0 to 65535' : helper}
          sx={{ width: 280 }}
        />
        <Button size="small" variant="outlined" onClick={add} disabled={!canAdd} sx={{ mt: 0.5 }}>
          Add
        </Button>
      </Box>
    </Box>
  );
};

const emptyDeviceRelations = {
  parent: null,
  ai_devices: [],
  associated_children: [],
  direct_children: [],
  candidates: [],
};

const DeviceDetails = ({ onLogout, canWriteBackhaul = false }) => {
  const { serial } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const serialRef = useRef(serial);
  serialRef.current = serial;

  const [relations, setRelations] = useState(emptyDeviceRelations);

  // Add effect to handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      // When browser back button is pressed, go to device list with preserved filters
      const searchParams = new URLSearchParams(location.search);
      const queryString = searchParams.toString();
      navigate(`/devices${queryString ? `?${queryString}` : ''}`, { replace: true });
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate, location.search]);

  const handleBack = () => {
    // Preserve filter state from the URL when navigating back
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    navigate(`/devices${queryString ? `?${queryString}` : ''}`, { replace: true });
  };

  const handleShowOnMap = () => {
    const deviceSerial = String(serial || '').trim();
    if (!deviceSerial) return;
    const params = new URLSearchParams({
      viewMode: 'map',
      deviceTypeFilter: 'all',
      searchQuery: deviceSerial,
    });
    navigate(`/devices?${params.toString()}`);
  };

  const [device, setDevice] = useState(null);
  const [segmentNames, setSegmentNames] = useState(() => new Map());

  useEffect(() => {
    let cancelled = false;
    api.getCustomTrees()
      .then((response) => {
        if (cancelled) return;
        const names = new Map();
        (Array.isArray(response?.trees) ? response.trees : []).forEach((tree) => {
          const id = Number(tree?.segment_id);
          const name = String(tree?.name || '').trim();
          if (Number.isFinite(id) && id > 0 && name) names.set(id, name);
        });
        setSegmentNames(names);
      })
      .catch(() => {
        if (!cancelled) setSegmentNames(new Map());
      });
    return () => { cancelled = true; };
  }, [serial]);

  const [detailTab, setDetailTab] = useState('info');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configJson, setConfigJson] = useState('');
  const [originalConfig, setOriginalConfig] = useState('');
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [applyingConfig, setApplyingConfig] = useState(false);
  // Bumped on dialog close / serial change so an abandoned poll cannot setState.
  const configApplyGenRef = useRef(0);
  const [lkgStatus, setLkgStatus] = useState({ type: '', message: '' });
  const [isJsonValid, setIsJsonValid] = useState(true);

  useEffect(() => {
    configApplyGenRef.current += 1;
    setApplyingConfig(false);
  }, [serial]);

  const [parentDialogOpen, setParentDialogOpen] = useState(false);
  const [accessListDialogOpen, setAccessListDialogOpen] = useState(false);
  const [selectedWhitelist, setSelectedWhitelist] = useState([]);
  const [selectedBlacklist, setSelectedBlacklist] = useState([]);
  const [parentUpdateStatus, setParentUpdateStatus] = useState({ type: '', message: '' });
  const [accessListUpdateStatus, setAccessListUpdateStatus] = useState({ type: '', message: '' });
  const [accessListInsistSec, setAccessListInsistSec] = useState('');
  const [whitelistDeviceFilter, setWhitelistDeviceFilter] = useState('');
  const [blacklistDeviceFilter, setBlacklistDeviceFilter] = useState('');
  const [parentDeviceFilter, setParentDeviceFilter] = useState('');
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedParent, setSelectedParent] = useState('');
  const [commandDialogOpen, setCommandDialogOpen] = useState(false);
  const [command, setCommand] = useState('');
  const [commandStatus, setCommandStatus] = useState({ type: '', message: '' });
  const [childAIDevices, setChildAIDevices] = useState([]);
  const [parentDevice, setParentDevice] = useState(null);
  const [childJnet2Devices, setChildJnet2Devices] = useState([]);
  const [childEdgeCoreDevices, setChildEdgeCoreDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [configMode, setConfigMode] = useState('fields');
  const [treeMgmtView, setTreeMgmtView] = useState(null);
  const [treeMgmtLoading, setTreeMgmtLoading] = useState(false);
  const [treeMgmtError, setTreeMgmtError] = useState(null);
  const [treeMgmtEdits, setTreeMgmtEdits] = useState({});
  const [stationClosedLoopEdits, setStationClosedLoopEdits] = useState({});
  // Device-level network_attributes edits (topology / static_ip).
  const [networkAttrEdits, setNetworkAttrEdits] = useState({});
  // Sub-tabs inside the Tree-Mgmt Fields dialog.
  const [fieldsSubTab, setFieldsSubTab] = useState('general');
  // Client Radio / AP Radio / Segments inside the WiFi tab.
  const [wifiInnerTab, setWifiInnerTab] = useState('client');
const [userInfo, setUserInfo] = useState(null);
const [environment, setEnvironment] = useState('');

  useEffect(() => {
    const info = auth.getUserInfo();
    setUserInfo(info);
    setEnvironment(config.getEnvironment());
  }, []);

  // Reset config state when serial changes (navigating to different device)
  useEffect(() => {
    setConfigJson('');
    setOriginalConfig('');
    setConfigError(null);
    setConfigMode('fields');
    setTreeMgmtView(null);
    setTreeMgmtError(null);
    setTreeMgmtEdits({});
    setStationClosedLoopEdits({});
    setNetworkAttrEdits({});
    setFieldsSubTab('general');
    setWifiInnerTab('client');
    setRelations(emptyDeviceRelations);
  }, [serial]);

  const handleLogoutClick = useCallback(() => {
    if (typeof onLogout === 'function') {
      onLogout();
    } else {
      auth.logout();
      window.location.assign('/');
    }
  }, [onLogout]);

  const getPeerAddress = (address) => {
    if (!address) return '';
    // Remove port number if present
    return address.split(':')[0];
  };

  const fetchDeviceData = useCallback(async () => {
    const requested = serial;
    try {
      setLoading(true);
      const relationsPromise = api.getDeviceRelations(serial).catch((err) => {
        console.error('Error fetching device relations:', err);
        return null;
      });
      const deviceData = await api.getDevice(serial);
      if (serialRef.current !== requested) return;
      setDevice(deviceData);
      setError(null);
      setLoading(false);
      const relationsData = await relationsPromise;
      if (serialRef.current !== requested) return;
      if (relationsData) setRelations(relationsData);
    } catch (err) {
      if (serialRef.current !== requested) return;
      console.error('Error fetching device data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch device data');
    } finally {
      if (serialRef.current === requested) setLoading(false);
    }
  }, [serial]);

  useEffect(() => {
    fetchDeviceData();
  }, [fetchDeviceData]);

  // Resolve AI device: prefer discovered AI children, then matching device by ai_device_mac, then fallback chip
  const resolvedAIDevices = useMemo(() => {
    const mac = device?.ai_device_mac?.toLowerCase();
    const serial = device?.serial_number?.toLowerCase();
    if (!mac || (serial && mac === serial)) return childAIDevices;

    // If we already have AI children detected, use them
    if (childAIDevices && childAIDevices.length > 0) return childAIDevices;

    // Try to find the AI device in the provided devices list
    const found = (relations?.ai_devices || []).find(d => d.serial_number && d.serial_number.toLowerCase() === mac);
    if (found) return [found];

    // Fallback: show a minimal placeholder so user sees the AI mapping
    return [{
      serial_number: device.ai_device_mac,
      connected: device.connected,
      firmware: device.firmware,
      last_seen: device.last_seen
    }];
  }, [childAIDevices, relations, device]);

  const meshParent = useMemo(() => {
    const parent = relations?.parent;
    if (!parent?.serial_number || !device?.parent_serial) return null;
    if (parent.serial_number.toLowerCase() !== device.parent_serial.toLowerCase()) return null;
    return parent;
  }, [relations, device]);

  const radioSectionPrivateIfaces = useMemo(
    () => filterRadioInterfacesByMode(device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces),
    [device]
  );
  const radioSectionPublicIfaces = useMemo(
    () => filterRadioInterfacesByMode(device?.public_wifi?.interfaces || device?.public_wifi?.Interfaces),
    [device]
  );

  const deviceDataCounters = useMemo(() => {
    if (!device) return null;
    return computeDeviceDataCounters(device, { isCap: isCapDevice(device, treeMgmtView) });
  }, [device, treeMgmtView]);

  const dataCounterRates = useMemo(() => getDataCounterRates(device), [device]);

  const showDataCountersSection = useMemo(() => {
    const priv = device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces || [];
    const pub = device?.public_wifi?.interfaces || device?.public_wifi?.Interfaces || [];
    const eth = device?.ethernet || device?.Ethernet || [];
    return priv.length > 0 || pub.length > 0 || eth.length > 0;
  }, [device]);

  const hasProvisioning = useMemo(() => {
    const prov = device?.provisioning;
    if (!prov) return false;
    return Boolean(
      prov.AiDeviceMac || prov.ai_device_mac
      || prov.Jnet1EmbeddedMac || prov.jnet1_embedded_mac
      || prov.DimmerEmbeddedSerial || prov.dimmer_embedded_serial
      || prov.Jnet1NemaMac || prov.jnet1_nema_mac
      || prov.DimmerNemaSerial || prov.dimmer_nema_serial
      || prov.FixtureSerial || prov.fixture_serial
    );
  }, [device]);

  const showConnectedAi = useMemo(() => {
    if (!resolvedAIDevices.length) return false;
    if ((device?.compatible || '').toLowerCase().includes('edgecore')) return false;
    const model = device?.capabilities?.model?.toLowerCase() || '';
    return !model.includes('nvidia') && !model.includes('qrb5165-128gb');
  }, [resolvedAIDevices, device]);

  const showParentDevice = useMemo(() => {
    if (!parentDevice) return false;
    const model = (device?.capabilities?.model || '').toString().toLowerCase();
    return model.includes('nvidia')
      || model.includes('qrb5165-128gb')
      || device?.compatible === 'edgecore_eap102'
      || device?.capabilities?.compatible === 'edgecore_eap102';
  }, [parentDevice, device]);

  const detailTabs = useMemo(() => [
    { id: 'info', label: 'Device Information' },
    showDataCountersSection && deviceDataCounters ? { id: 'counters', label: 'Data Counters' } : null,
    { id: 'notes', label: 'Notes' },
    hasProvisioning ? { id: 'provisioning', label: 'Provisioning' } : null,
    showConnectedAi ? { id: 'ai', label: 'Connected AI' } : null,
    childJnet2Devices.length > 0 ? { id: 'children', label: 'Associated Children' } : null,
    childEdgeCoreDevices.length > 0 && (device?.compatible || '').toLowerCase().includes('edgecore')
      ? { id: 'edgecore-children', label: 'Direct Children' }
      : null,
    showParentDevice ? { id: 'parent', label: 'Parent' } : null,
    (radioSectionPrivateIfaces.length > 0 || radioSectionPublicIfaces.length > 0)
      ? { id: 'radios', label: 'Radio Interfaces' }
      : null,
    { id: 'commands', label: 'Commands' },
  ].filter(Boolean), [
    showDataCountersSection,
    deviceDataCounters,
    hasProvisioning,
    showConnectedAi,
    childJnet2Devices.length,
    childEdgeCoreDevices.length,
    device,
    showParentDevice,
    radioSectionPrivateIfaces.length,
    radioSectionPublicIfaces.length,
  ]);

  useEffect(() => {
    if (!detailTabs.some((tab) => tab.id === detailTab)) {
      setDetailTab('info');
    }
  }, [detailTabs, detailTab]);

  const filterAccessListDevices = useCallback((devices, query) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return devices;
    return devices.filter((d) => {
      const name = (d.name || '').toLowerCase();
      const serial = (d.serial_number || '').toLowerCase();
      return name.includes(normalized) || serial.includes(normalized);
    });
  }, []);

  const filteredWhitelistDevices = useMemo(
    () => filterAccessListDevices(availableDevices, whitelistDeviceFilter),
    [availableDevices, whitelistDeviceFilter, filterAccessListDevices]
  );

  const filteredBlacklistDevices = useMemo(
    () => filterAccessListDevices(availableDevices, blacklistDeviceFilter),
    [availableDevices, blacklistDeviceFilter, filterAccessListDevices]
  );

  const filteredParentDevices = useMemo(
    () => filterAccessListDevices(availableDevices, parentDeviceFilter),
    [availableDevices, parentDeviceFilter, filterAccessListDevices]
  );

  const renderAccessListSelectFilter = (value, onChange) => (
    <ListSubheader
      sx={{
        lineHeight: 1,
        py: 1,
        px: 1.5,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        bgcolor: 'background.paper',
      }}
    >
      <TextField
        size="small"
        placeholder="Type to filter..."
        fullWidth
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
    </ListSubheader>
  );

  useEffect(() => {
    if (!device) return;
    const aiSource = relations?.ai_devices || [];
    const aiChildren = aiSource.filter((d) => {
      const modelLower = (d?.capabilities?.model || '').toString().toLowerCase();
      const isAI = modelLower.includes('nvidia') || modelLower.includes('qrb5165-128gb');
      const isChild = d.parent_serial
        && device.serial_number
        && d.parent_serial.toLowerCase() === device.serial_number.toLowerCase();
      return isAI && isChild;
    });
    setChildAIDevices(aiChildren);

    // jnet2 children come back already matched to this AP's associations.
    setChildJnet2Devices(relations?.associated_children || []);

    // EdgeCore children: attach this AP's association stats to the matched devices.
    const edgeCoreChildren = [];
    const privIfaces = device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces || [];
    const stationAssocMap = new Map();
    privIfaces.forEach((iface) => {
      if (!isAPModeIface(iface)) return;
      const assocs = iface.Associations || iface.associations || [];
      assocs.forEach((assoc) => {
        const stationRaw = assoc.Station || assoc.station;
        if (!stationRaw) return;
        const stationKey = normalizeStationKey(stationRaw);
        if (stationKey) {
          stationAssocMap.set(stationKey, {
            rssi: assoc.RSSI || assoc.rssi,
            connectedTime: assoc.ConnectedTime || assoc.connected_time || 0,
            rxBytes: assoc.RxBytes || assoc.rx_bytes || 0,
            txBytes: assoc.TxBytes || assoc.tx_bytes || 0,
            rxPackets: assoc.RxPackets || assoc.rx_packets || 0,
            txPackets: assoc.TxPackets || assoc.tx_packets || 0,
            rxRate: assoc.RxRate || assoc.rx_rate,
            txRate: assoc.TxRate || assoc.tx_rate,
            bssid: assoc.BSSID || assoc.bssid,
          });
        }
      });
    });

    if (stationAssocMap.size > 0) {
      (relations?.direct_children || []).forEach((d) => {
        const matchedKey = deviceAssociationMatchKeys(d).find((k) => stationAssocMap.has(k));
        if (!matchedKey) return;
        const assocData = stationAssocMap.get(matchedKey);
        if (!assocData) return;
        edgeCoreChildren.push({
          ...d,
          edgeCoreAssoc: assocData,
          edgeCoreOnline: true,
        });
        stationAssocMap.delete(matchedKey);
      });
    }
    setChildEdgeCoreDevices(edgeCoreChildren);

    if (device.parent_serial) {
      const modelLower = (device?.capabilities?.model || '').toString().toLowerCase();
      const isAI = modelLower.includes('nvidia') || modelLower.includes('qrb5165-128gb');
      const isEdgeCoreChild = device.compatible === 'edgecore_eap102'
        || (device.capabilities?.compatible === 'edgecore_eap102');
      if (isAI || isEdgeCoreChild) {
        const parent = relations?.parent;
        const parentMatches = parent?.serial_number
          && parent.serial_number.toLowerCase() === device.parent_serial.toLowerCase();
        setParentDevice(parentMatches ? parent : null);
      } else {
        setParentDevice(null);
      }
    } else {
      setParentDevice(null);
    }
  }, [relations, device]);

  // Add new function to get connection details
  const getConnectionDetails = useCallback((deviceToCheck, parentDeviceToCheck) => {
    if (!deviceToCheck || !parentDeviceToCheck) return null;

    const deviceIdentifiers = new Set(deviceAssociationMatchKeys(deviceToCheck));
    if (deviceIdentifiers.size === 0) {
      return null;
    }

    // Search through private and public WiFi interfaces of the parent
    const searchInterfaces = (interfaces, wifiType) => {
      if (!interfaces) {
        return null;
      }
      
      for (const iface of interfaces) {
        // Handle both Associations (capital) and associations (lowercase) for compatibility
        const associations = iface.Associations || iface.associations;
        if (!associations) continue;
        
        for (const assoc of associations) {
          // Handle both Station/station for compatibility
          const station = assoc.Station || assoc.station;
          if (!station) continue;

          const stationKey = normalizeStationKey(station);

          // Get RSSI (handle both RSSI/rssi), rx_rate, tx_rate with case compatibility
          const rssiValue = assoc.RSSI || assoc.rssi;
          const rxRate = assoc.RxRate || assoc.rx_rate;
          const txRate = assoc.TxRate || assoc.tx_rate;

          if (stationKey && deviceIdentifiers.has(stationKey)) {
              return {
              rssi: rssiValue, // This is AP → STA RSSI (TX from AP perspective)
              rxRate: rxRate,
              txRate: txRate
            };
          }
          }
      }
      return null;
    };

    // Get AP → STA RSSI (RX) from the device's station interface
    const getStationRssi = (device) => {
      const privateIfaces = device.private_wifi?.interfaces || device.private_wifi?.Interfaces;
      if (!privateIfaces) return null;
      // Handle both Mode/mode for compatibility (jnet2 uses lowercase)
      const stationInterface = privateIfaces.find(iface => 
        (iface.Mode || iface.mode)?.toLowerCase() === 'station'
      );
      // Handle both RSSI/rssi for compatibility
      return stationInterface?.RSSI || stationInterface?.rssi;
    };

    // Search in both private and public WiFi of the parent
    const connectionDetails = searchInterfaces(parentDeviceToCheck.private_wifi?.interfaces || parentDeviceToCheck.private_wifi?.Interfaces, 'private_wifi') ||
                            searchInterfaces(parentDeviceToCheck.public_wifi?.interfaces || parentDeviceToCheck.public_wifi?.Interfaces, 'public_wifi');

    if (connectionDetails) {
      return {
        ...connectionDetails,
        stationRssi: getStationRssi(deviceToCheck) // Add AP → STA RSSI
      };
    }

    return null;
  }, []);

  // Prefer the child's STA-mode `uplink` profile (RSSI + radio TX/RX
  // rate histograms) over the parent's AP-side Association entry for
  // MCS/NSS/BW/rate. The AP-side association sums per-station rate
  // samples across every client on the same parent radio, so MCS,
  // NSS and BW are aliased and effectively unusable for per-edge
  // attribution. The child's STA radio histograms describe THIS
  // single edge (the child has exactly one STA interface), so they
  // are the authoritative source the optimizer also consumes.
  //
  // Falls back to the legacy parent-side association data for any
  // device that does not yet have an `uplink` block populated
  // (legacy firmware that didn't report enough counters, EdgeCore
  // children, or jnet2 devices whose STA interface has no SSID set
  // yet — e.g. immediately after boot before the mesh is joined).
  const getUplinkDetails = useCallback((childDevice, parentDevice) => {
    const fallback = getConnectionDetails(childDevice, parentDevice);
    const u = childDevice?.uplink;
    if (!u || (!u.tx_profile && !u.rx_profile && !u.rssi)) {
      return fallback;
    }

    // Convert a RadioRateProfile (as returned by the backend
    // `enrichUplinkForResponse`) into the legacy `{Bitrate, NSS,
    // MCS, BW}` shape the existing render code already understands.
    // `Bitrate` here is an estimated PHY rate derived from the
    // dominant MCS/NSS/BW tuple, not measured application throughput.
    const toRate = (profile) => {
      if (!profile) return null;
      const out = {};
      const mcs = profile.data_dominant_mcs || profile.dominant_mcs;
      if (mcs) out.MCS = mcs;
      if (profile.dominant_nss) out.NSS = profile.dominant_nss;
      if (profile.dominant_bw_mhz) out.BW = profile.dominant_bw_mhz;
      // est_kbps comes from algo.UplinkProfileKbps server-side.
      // Convert kbps → bps to match the legacy `Bitrate` units the
      // render code divides by 1_000_000 for Mbps display.
      if (profile.est_kbps) out.Bitrate = profile.est_kbps * 1000;
      if (profile.est_kbps) out.Estimated = true;
      return Object.keys(out).length ? out : null;
    };

    const tx = toRate(u.tx_profile);
    const rx = toRate(u.rx_profile);

    // The STA-mode interface reports a single RSSI (child's view of
    // the parent AP). We surface it as both `rssi` (the original
    // "TX direction" RSSI label) and `stationRssi` (the original
    // "RX direction" label) because the new pipeline doesn't
    // distinguish a separate AP→STA vs STA→AP RSSI per direction —
    // it's the same SNR measurement either way.
    const childRssi = u.rssi || null;
    return {
      rssi: childRssi || fallback?.rssi || null,
      stationRssi: childRssi || fallback?.stationRssi || null,
      rxRate: rx || fallback?.rxRate || null,
      txRate: tx || fallback?.txRate || null,
      source: (tx || rx || childRssi) ? 'uplink' : 'association',
      txTotalBytes: u.tx_profile?.total_bytes,
      rxTotalBytes: u.rx_profile?.total_bytes,
      parentSSID: u.parent_ssid,
    };
  }, [getConnectionDetails]);

  const childLinkCardTitle = useCallback((perspective, source) => {
    const base = perspective === 'ap' ? 'AP hears child' : 'Child hears AP';
    return source === 'association' ? `${base} · AP assoc` : base;
  }, []);

  // New function to fetch configuration
  const fetchDeviceConfig = async () => {
    try {
      setConfigLoading(true);
      setConfigError(null);
      console.log('Fetching device configuration for serial:', serial);
      const configData = await api.getDeviceConfig(serial);
      console.log('Config data received:', configData);
      const formattedConfig = JSON.stringify(configData.configuration || {}, null, 2);
      setConfigJson(formattedConfig);
      setOriginalConfig(formattedConfig);
    } catch (err) {
      console.error('Error fetching device configuration:', err);
      setConfigError(err.response?.data?.message || err.message || 'Failed to fetch device configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  const fetchTreeMgmtView = useCallback(async () => {
    try {
      setTreeMgmtLoading(true);
      setTreeMgmtError(null);
      const view = await api.getTreeMgmtConfig(serial);
      setTreeMgmtView(view);
      setTreeMgmtEdits({});
      setStationClosedLoopEdits({});
      setNetworkAttrEdits({});
    } catch (err) {
      console.error('Error fetching tree-mgmt view:', err);
      setTreeMgmtError(err.message || 'Failed to fetch tree-management view');
    } finally {
      setTreeMgmtLoading(false);
    }
  }, [serial]);

  const setTreeEdit = (treeId, field, value) => {
    setTreeMgmtEdits((prev) => {
      const next = { ...prev };
      const entry = { ...(next[treeId] || {}) };
      if (value === undefined || value === null) {
        delete entry[field];
      } else {
        // Keep empty string so numeric fields can be cleared while typing.
        entry[field] = value;
      }
      if (Object.keys(entry).length === 0) {
        delete next[treeId];
      } else {
        next[treeId] = entry;
      }
      return next;
    });
  };

  const parseEditedInt = (raw, treeId, label) => {
    if (raw === '') {
      return { error: `${treeId}: ${label} cannot be empty` };
    }
    if (label === 'self_improvment' && isBgSelfImprovementAlways(raw)) {
      return { value: BG_SELF_IMPROVEMENT_ALWAYS };
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) {
      return { error: `${treeId}: ${label} must be an integer` };
    }
    return { value: n };
  };

  // Returns 'single' | 'group_ids' | 'groups' for the tree, preferring the
  // user's pending edits and falling back to the server view.
  const channelModeForTree = (tree) => {
    const edits = treeMgmtEdits[tree.tree_id] || {};
    if (edits.channel_mode) return edits.channel_mode;
    if (Array.isArray(tree.channel_group_ids) && tree.channel_group_ids.length > 0) {
      return 'group_ids';
    }
    if (tree.channel_groups && Object.keys(tree.channel_groups).length > 0) {
      return 'groups';
    }
    return 'single';
  };

  // Best-effort parser for the channel_group_ids edit value (which may be a
  // CSV string, an array, or even an array of {id} options from Autocomplete).
  // Filters down to the valid known UNII ids; never returns undefined so the
  // UI can always render the multi-select state.
  const parseChannelGroupIds = (raw) => {
    if (raw === '' || raw === undefined || raw === null) return [];
    let arr;
    if (Array.isArray(raw)) {
      arr = raw.map((v) => (typeof v === 'object' && v !== null ? v.id : v));
    } else {
      arr = String(raw).split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    }
    const out = [];
    for (const v of arr) {
      const n = parseInt(v, 10);
      if (!Number.isNaN(n) && UNII_BY_ID[n]) out.push(n);
    }
    return out;
  };

  // Parses a CSV/space separated list of integers; returns undefined on error
  // (caller decides whether to surface validation).
  const parseIntList = (raw) => {
    if (raw === '' || raw === undefined || raw === null) return [];
    const parts = String(raw).split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    const out = [];
    for (const p of parts) {
      const n = parseInt(p, 10);
      if (Number.isNaN(n)) return undefined;
      out.push(n);
    }
    return out;
  };

  // Returns the value to show in the input for a given (tree_id, field): the
  // pending edit if present, otherwise the value from the server view.
  const treeFieldValue = (tree, field) => {
    const edits = treeMgmtEdits[tree.tree_id] || {};
    if (Object.prototype.hasOwnProperty.call(edits, field)) {
      return edits[field];
    }
    const v = tree[field];
    if (v === undefined || v === null) return '';
    return typeof v === 'number' ? String(v) : v;
  };

  // Station TX is stored on the mode:"sta" entry (txpower, first priority) and/or
  // the AP row (txpower_up, fallback). The API exposes one tx_power_up using
  // EffectiveStationTxPower so the UI reads and edits a single value.
  const clientRadioTree = () => {
    const trees = treeMgmtView?.trees || [];
    if (trees.length === 0) return null;
    const withTx = trees.find((t) => treeFieldValue(t, 'tx_power_up') !== '');
    return withTx || trees[0];
  };

  const showClientRadioTab = treeMgmtView?.device_type !== 'cap' && Boolean(clientRadioTree());
  const wifiTab = (wifiInnerTab === 'client' && !showClientRadioTab) ? 'ap' : wifiInnerTab;

  const TOPOLOGY_OPTIONS = [
    {
      value: '1',
      label: 'Standard',
      helper: 'Device does not run a DHCP server',
      roles: ['node', 'cap'],
    },
    {
      value: '2',
      label: 'Local peripherals DHCP',
      helper: 'Node runs DHCP on the local bridge for physical peripherals (e.g. AI board)',
      roles: ['node'],
    },
    {
      value: '3',
      label: 'Wi-Fi tree & local peripherals DHCP',
      helper: 'CAP runs DHCP on wireless interfaces for the whole Wi-Fi tree and on the local bridge for physical peripherals',
      roles: ['cap'],
    },
  ];

  const isValidIPv4 = (value) => {
    const parts = String(value ?? '').trim().split('.');
    if (parts.length !== 4) return false;
    return parts.every((p) => {
      if (!/^\d{1,3}$/.test(p)) return false;
      const n = Number(p);
      return n >= 0 && n <= 255;
    });
  };

  const topologyValue = () =>
    networkAttrEdits.topology !== undefined
      ? networkAttrEdits.topology
      : (treeMgmtView?.topology || '1');

  const isStaticIpEnabled = () =>
    networkAttrEdits.use_static_ip !== undefined
      ? networkAttrEdits.use_static_ip
      : Boolean(treeMgmtView?.static_ip);

  const staticIpFieldValue = (field) => {
    const draft = networkAttrEdits.static_ip;
    if (draft && typeof draft === 'object' && draft[field] !== undefined) {
      return draft[field];
    }
    return treeMgmtView?.static_ip?.[field] || '';
  };

  const setTopologyEdit = (value) => {
    setNetworkAttrEdits((prev) => {
      const next = { ...prev };
      const baseline = treeMgmtView?.topology || '1';
      if (value === baseline) {
        delete next.topology;
      } else {
        next.topology = value;
      }
      return next;
    });
  };

  const setUseStaticIpEdit = (enabled) => {
    setNetworkAttrEdits((prev) => {
      const next = { ...prev };
      const wasOn = Boolean(treeMgmtView?.static_ip);
      if (!enabled) {
        delete next.static_ip;
        if (wasOn) {
          next.use_static_ip = false;
        } else {
          delete next.use_static_ip;
        }
        return next;
      }
      if (wasOn) {
        delete next.use_static_ip;
      } else {
        next.use_static_ip = true;
      }
      if (!next.static_ip) {
        next.static_ip = {
          ipaddr: treeMgmtView?.static_ip?.ipaddr || '',
          netmask: treeMgmtView?.static_ip?.netmask || '',
          gateway: treeMgmtView?.static_ip?.gateway || '',
          dns: treeMgmtView?.static_ip?.dns || '',
        };
      }
      return next;
    });
  };

  const setStaticIpFieldEdit = (field, value) => {
    setNetworkAttrEdits((prev) => {
      const next = { ...prev, use_static_ip: true };
      const draft = {
        ipaddr: treeMgmtView?.static_ip?.ipaddr || '',
        netmask: treeMgmtView?.static_ip?.netmask || '',
        gateway: treeMgmtView?.static_ip?.gateway || '',
        dns: treeMgmtView?.static_ip?.dns || '',
        ...(prev.static_ip && typeof prev.static_ip === 'object' ? prev.static_ip : {}),
        [field]: value,
      };
      next.static_ip = draft;
      return next;
    });
  };

  const hasPendingNetworkAttrEdits = () => {
    if (networkAttrEdits.topology !== undefined) return true;
    const wasOn = Boolean(treeMgmtView?.static_ip);
    if (networkAttrEdits.use_static_ip !== undefined && networkAttrEdits.use_static_ip !== wasOn) {
      return true;
    }
    if (networkAttrEdits.static_ip && typeof networkAttrEdits.static_ip === 'object') {
      for (const field of ['ipaddr', 'netmask', 'gateway', 'dns']) {
        if (networkAttrEdits.static_ip[field] === undefined) continue;
        const orig = treeMgmtView?.static_ip?.[field] || '';
        if (String(networkAttrEdits.static_ip[field]) !== String(orig)) return true;
      }
    }
    return false;
  };

  const buildTreeMgmtPatch = () => {
    if (!treeMgmtView) return { trees: [], errors: [], topology: undefined, static_ip: undefined };
    const trees = [];
    const errors = [];
    for (const tree of treeMgmtView.trees || []) {
      const edits = treeMgmtEdits[tree.tree_id];
      if (!edits || Object.keys(edits).length === 0) continue;
      const entry = { tree_id: tree.tree_id };
      let touched = false;
      if (edits.band !== undefined) { entry.band = edits.band; touched = true; }
      if (edits.htmode !== undefined) { entry.htmode = edits.htmode; touched = true; }
      if (edits.tx_power_up !== undefined) {
        const parsed = parseEditedInt(edits.tx_power_up, tree.tree_id, 'tx_power_up');
        if (parsed.error) { errors.push(parsed.error); continue; }
        entry.tx_power_up = parsed.value;
        touched = true;
      }
      if (edits.tx_power_down !== undefined) {
        const parsed = parseEditedInt(edits.tx_power_down, tree.tree_id, 'tx_power_down');
        if (parsed.error) { errors.push(parsed.error); continue; }
        entry.tx_power_down = parsed.value;
        touched = true;
      }
      if (edits.max_children !== undefined) {
        const parsed = parseEditedInt(edits.max_children, tree.tree_id, 'max_children');
        if (parsed.error) { errors.push(parsed.error); continue; }
        entry.max_children = parsed.value;
        touched = true;
      }

      // Channel selection — only emit the field for the currently-active mode.
      // When in a band-bound mode (group_ids / groups) we also drop any
      // selectors from the other band so we never round-trip a cross-band
      // selection through the API.
      const mode = channelModeForTree(tree);
      const effectiveBand = (edits.band !== undefined ? edits.band : tree.band) || '';
      if (mode === 'single' && edits.channel !== undefined) {
        const parsed = parseEditedInt(edits.channel, tree.tree_id, 'channel');
        if (parsed.error) { errors.push(parsed.error); continue; }
        entry.channel = parsed.value;
        touched = true;
      } else if (mode === 'group_ids' && edits.channel_group_ids !== undefined) {
        const ids = parseIntList(edits.channel_group_ids);
        if (ids === undefined) {
          errors.push(`${tree.tree_id}: channel_group_ids must be a list of integers`);
          continue;
        }
        const filtered = effectiveBand
          ? ids.filter(id => UNII_BY_ID[id]?.band === effectiveBand)
          : ids;
        entry.channel_group_ids = filtered;
        touched = true;
      } else if (mode === 'groups') {
        const groups = {};
        let groupTouched = false;
        for (const band of ['5GHz', '6GHz']) {
          if (effectiveBand && band !== effectiveBand) continue;
          const key = `channel_groups_${band}`;
          if (edits[key] !== undefined) {
            const list = parseIntList(edits[key]);
            if (list === undefined) {
              errors.push(`${tree.tree_id}: channel_groups[${band}] must be a list of integers`);
              continue;
            }
            groups[band] = list;
            groupTouched = true;
          }
        }
        if (groupTouched) {
          entry.channel_groups = groups;
          touched = true;
        }
      }

      // Background STA scan — pull out bg_* edits into a sub-object.
      const bg = {};
      let bgTouched = false;
      let bgError = false;
      const bgFields = [
        ['bg_bg_scan_on_off', 'bg_scan_on_off', 'string'],
        ['bg_chan_scan_interval', 'chan_scan_interval', 'int'],
        ['bg_fr_on_off', 'fr_on_off', 'string'],
        ['bg_scan_interval', 'scan_interval', 'int'],
        ['bg_self_improvment', 'self_improvment', 'int'],
      ];
      for (const [editKey, apiKey, kind] of bgFields) {
        if (edits[editKey] === undefined) continue;
        if (kind === 'int') {
          const parsed = parseEditedInt(edits[editKey], tree.tree_id, apiKey);
          if (parsed.error) {
            errors.push(parsed.error);
            bgError = true;
            break;
          }
          bg[apiKey] = parsed.value;
        } else {
          bg[apiKey] = edits[editKey];
        }
        bgTouched = true;
      }
      if (bgError) continue;
      if (bgTouched) {
        entry.background_sta_scan = bg;
        touched = true;
      }

      if (edits.segment_id_ap !== undefined) {
        entry.segment_id_ap = edits.segment_id_ap;
        touched = true;
      }

      if (touched) trees.push(entry);
    }

    if (treeMgmtView.device_type !== 'cap') {
      const devDraft = treeMgmtEdits[STATION_SEGMENT_EDIT_KEY];
      if (devDraft && Array.isArray(devDraft.segment_id_dev)) {
        const host = (treeMgmtView.trees || [])[0];
        if (!host) {
          errors.push('Station segments need a treemgmt entry');
        } else {
          let entry = trees.find((t) => t.tree_id === host.tree_id);
          if (!entry) {
            entry = { tree_id: host.tree_id };
            trees.push(entry);
          }
          entry.segment_id_dev = devDraft.segment_id_dev;
        }
      }
    }

    if (treeMgmtView.supports_station_closed_loop) {
      const loopFields = ['station_target_min_ap_rssi', 'station_target_max_ap_rssi'];
      const hasLoopEdits = loopFields.some((f) => stationClosedLoopEdits[f] !== undefined);
      if (hasLoopEdits) {
        const radioTree = clientRadioTree();
        if (radioTree) {
          let entry = trees.find((t) => t.tree_id === radioTree.tree_id);
          if (!entry) {
            entry = { tree_id: radioTree.tree_id };
            trees.push(entry);
          }
          for (const field of loopFields) {
            if (stationClosedLoopEdits[field] === undefined) continue;
            const parsed = parseStationLoopRssiEdit(stationClosedLoopEdits[field], field);
            if (parsed.error) {
              errors.push(parsed.error);
              continue;
            }
            entry[field] = parsed.value;
          }
          if (
            entry.station_target_min_ap_rssi !== undefined &&
            entry.station_target_max_ap_rssi !== undefined &&
            entry.station_target_min_ap_rssi > entry.station_target_max_ap_rssi
          ) {
            errors.push('Client Radio: target min AP RSSI must be <= target max AP RSSI');
          }
        }
      }
    }

    let topology;
    let staticIp;
    if (networkAttrEdits.topology !== undefined) {
      const role = treeMgmtView.device_type === 'cap' ? 'cap' : 'node';
      const opt = TOPOLOGY_OPTIONS.find((o) => o.value === networkAttrEdits.topology);
      if (!opt || !opt.roles.includes(role)) {
        errors.push(`Topology is not allowed for this ${role === 'cap' ? 'CAP' : 'node'} device`);
      } else {
        topology = networkAttrEdits.topology;
      }
    }

    const wasStaticOn = Boolean(treeMgmtView.static_ip);
    const staticEnabled = isStaticIpEnabled();
    const staticFieldsDirty =
      staticEnabled &&
      networkAttrEdits.static_ip &&
      typeof networkAttrEdits.static_ip === 'object' &&
      ['ipaddr', 'netmask', 'gateway', 'dns'].some((field) => {
        if (networkAttrEdits.static_ip[field] === undefined) return false;
        const orig = treeMgmtView?.static_ip?.[field] || '';
        return String(networkAttrEdits.static_ip[field]) !== String(orig);
      });
    if (staticEnabled !== wasStaticOn || staticFieldsDirty) {
      if (!staticEnabled) {
        staticIp = null;
      } else {
        const sip = {
          ipaddr: String(staticIpFieldValue('ipaddr')).trim(),
          netmask: String(staticIpFieldValue('netmask')).trim(),
          gateway: String(staticIpFieldValue('gateway')).trim(),
          dns: String(staticIpFieldValue('dns')).trim(),
        };
        const labels = {
          ipaddr: 'IP address',
          netmask: 'Netmask',
          gateway: 'Gateway',
          dns: 'DNS',
        };
        let sipOk = true;
        for (const field of ['ipaddr', 'netmask', 'gateway', 'dns']) {
          if (!sip[field]) {
            errors.push(`Static IP: ${labels[field]} is required`);
            sipOk = false;
          } else if (!isValidIPv4(sip[field])) {
            errors.push(`Static IP: ${labels[field]} must be a valid IPv4 address`);
            sipOk = false;
          }
        }
        if (sipOk) {
          staticIp = sip;
        }
      }
    }

    return { trees, errors, topology, static_ip: staticIp };
  };

  const hasPendingTreeMgmtEdits = () =>
    Object.keys(treeMgmtEdits).length > 0 ||
    Object.keys(stationClosedLoopEdits).length > 0 ||
    hasPendingNetworkAttrEdits();

  const handleSaveTreeMgmt = async () => {
    const built = buildTreeMgmtPatch();
    if (built.errors && built.errors.length > 0) {
      setSaveStatus({ type: 'error', message: built.errors.join('; ') });
      return;
    }
    const hasNetworkPatch = built.topology !== undefined || built.static_ip !== undefined;
    if (built.trees.length === 0 && !hasNetworkPatch) {
      setSaveStatus({ type: 'warning', message: 'No field changes to apply' });
      return;
    }

    const patchBody = {};
    if (built.trees.length > 0) {
      patchBody.trees = built.trees;
    }
    if (built.topology !== undefined) {
      patchBody.topology = built.topology;
    }
    if (built.static_ip !== undefined) {
      patchBody.static_ip = built.static_ip;
    }

    const pushedAtMs = Date.now();
    const applyGen = ++configApplyGenRef.current;
    try {
      const resp = await api.patchTreeMgmtConfig(serial, patchBody);
      if (configApplyGenRef.current !== applyGen) {
        return;
      }

      if (resp && resp.view) {
        setTreeMgmtView(resp.view);
        setTreeMgmtEdits({});
        setStationClosedLoopEdits({});
        setNetworkAttrEdits({});
        const patchedSegments = (patchBody.trees || []).some(
          (tree) => tree.segment_id_ap !== undefined || tree.segment_id_dev !== undefined,
        );
        if (patchedSegments) {
          const apIds = [];
          for (const tree of resp.view.trees || []) {
            for (const id of tree.segment_id_ap || []) {
              if (!apIds.includes(id)) apIds.push(id);
            }
          }
          const devIds = resp.view.device_type === 'cap'
            ? undefined
            : (resp.view.segment_id_dev || []);
          setDevice((prev) => {
            if (!prev) return prev;
            const segment = { ...(prev.segment || {}), segment_id_ap: apIds };
            if (devIds !== undefined) segment.segment_id_dev = devIds;
            return { ...prev, segment };
          });
        }
      }

      const commandUuid = resp?.command_uuid;
      // PATCH persists even when the device is offline (empty command_uuid).
      if (!commandUuid) {
        setSaveStatus({
          type: 'warning',
          message: 'Tree-management settings saved but not sent (device offline)',
        });
        setTimeout(() => {
          if (configApplyGenRef.current !== applyGen) {
            return;
          }
          setConfigDialogOpen(false);
          setSaveStatus({ type: '', message: '' });
        }, 2000);
        return;
      }

      setApplyingConfig(true);
      setSaveStatus({ type: 'info', message: 'Tree-management settings saved. Applying on device…' });

      const result = await pollConfigApplied(commandUuid, pushedAtMs, applyGen);
      if (configApplyGenRef.current !== applyGen || !result) {
        return;
      }
      setApplyingConfig(false);
      setSaveStatus(
        result.type === 'success'
          ? { type: 'success', message: 'Tree-management settings applied by device' }
          : result,
      );

      const closeDelay = result.type === 'success' ? 1500 : 4000;
      setTimeout(() => {
        if (configApplyGenRef.current !== applyGen) {
          return;
        }
        setConfigDialogOpen(false);
        setSaveStatus({ type: '', message: '' });
      }, closeDelay);
    } catch (err) {
      if (configApplyGenRef.current !== applyGen) {
        return;
      }
      setApplyingConfig(false);
      setSaveStatus({
        type: 'error',
        message: err?.data?.error || err?.message || 'Failed to apply tree-management settings',
      });
    }
  };

  const sanitizeStationLoopRssiDraft = (raw) => String(raw ?? '').replace(/\D/g, '');

  const formatStationLoopRssiForDisplay = (value) => {
    if (value === undefined || value === null || value === '') return '';
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return String(Math.abs(Math.trunc(n)));
  };

  const parseStationLoopRssiEdit = (raw, label) => {
    const draft = sanitizeStationLoopRssiDraft(raw);
    if (draft === '') {
      return { error: `Client Radio: ${label} cannot be empty` };
    }
    const magnitude = parseInt(draft, 10);
    if (Number.isNaN(magnitude)) {
      return { error: `Client Radio: ${label} must be a non-negative integer` };
    }
    return { value: magnitude === 0 ? 0 : -magnitude };
  };

  const stationLoopFieldValue = (field) => {
    if (Object.prototype.hasOwnProperty.call(stationClosedLoopEdits, field)) {
      return stationClosedLoopEdits[field];
    }
    return formatStationLoopRssiForDisplay(treeMgmtView?.[field]);
  };

  const setStationLoopEdit = (field, value) => {
    setStationClosedLoopEdits((prev) => ({
      ...prev,
      [field]: sanitizeStationLoopRssiDraft(value),
    }));
  };

  const blockStationLoopRssiInputKeys = (event) => {
    if (event.key === '-' || event.key === '+' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  };

  const stationLoopRssiInputProps = () => ({
    startAdornment: <InputAdornment position="start">-</InputAdornment>,
  });

  // Returns the displayed value for one channel-group-ids / channel-groups
  // input box (always a string suitable for a TextField). Pending edits win
  // over the server view.
  const channelGroupIdsValue = (tree) => {
    const edits = treeMgmtEdits[tree.tree_id] || {};
    if (Object.prototype.hasOwnProperty.call(edits, 'channel_group_ids')) {
      return edits.channel_group_ids;
    }
    const ids = tree.channel_group_ids;
    if (!Array.isArray(ids)) return '';
    return ids.join(', ');
  };

  const channelGroupsValueForBand = (tree, band) => {
    const edits = treeMgmtEdits[tree.tree_id] || {};
    const key = `channel_groups_${band}`;
    if (Object.prototype.hasOwnProperty.call(edits, key)) {
      return edits[key];
    }
    const groups = tree.channel_groups || {};
    const list = groups[band];
    if (!Array.isArray(list)) return '';
    return list.join(', ');
  };

  // Returns the displayed value for one background_sta_scan sub-field.
  const bgFieldValue = (tree, apiKey) => {
    const editKey = `bg_${apiKey}`;
    const edits = treeMgmtEdits[tree.tree_id] || {};
    if (Object.prototype.hasOwnProperty.call(edits, editKey)) {
      return edits[editKey];
    }
    const bg = tree.background_sta_scan || {};
    const v = bg[apiKey];
    if (v === undefined || v === null) return '';
    return typeof v === 'number' ? String(v) : v;
  };

  const bgSelfImprovementDisplay = (tree) => formatBgSelfImprovementDisplay(bgFieldValue(tree, 'self_improvment'));

  const bgChannelGroupsDisplayForBand = (tree, band) => {
    const bg = tree.background_sta_scan || {};
    const groups = bg.channel_groups || {};
    const list = groups[band];
    if (!Array.isArray(list) || list.length === 0) return '—';
    return list.join(', ');
  };

  // ---- Add Tree dialog ----
  // `channel_mode` mirrors the inline editor: 'single' (channel), 'group_ids'
  // (UNII multi-select), or 'groups' (explicit per-band channel lists). The
  // submit handler emits exactly one of (channel | channel_group_ids |
  // channel_groups) based on this mode so we don't trip the backend's
  // mutual-exclusion validation.
  const [addTreeOpen, setAddTreeOpen] = useState(false);
  const [addTreeDraft, setAddTreeDraft] = useState({
    tree_id: '',
    band: '5GHz',
    channel_mode: 'single',
    channel: '',
    channel_group_ids: [],
    channel_groups_5GHz: '',
    channel_groups_6GHz: '',
    htmode: '',
    tx_power_up: '',
    tx_power_down: '',
    max_children: '',
  });
  const [addTreeStatus, setAddTreeStatus] = useState({ type: '', message: '' });

  // Backend cap: Node = 1 treemgmt entry, CAP = 2.
  const maxAllowedTrees = () => {
    if (!treeMgmtView) return 0;
    return treeMgmtView.device_type === 'cap' ? 2 : 1;
  };

  // Returns the radio names defined on the device that don't yet have a
  // matching treemgmt entry. On Node devices the 2.4GHz radio is excluded
  // (it cannot host treemgmt entries). Returns [] if we can't parse the
  // raw config — the backend will still validate.
  const availableTreeIDs = () => {
    if (!treeMgmtView) return [];
    const isCap = treeMgmtView.device_type === 'cap';
    let radios = [];
    try {
      const cfg = JSON.parse(configJson || '{}');
      if (Array.isArray(cfg.radio)) {
        radios = cfg.radio
          .filter(r => r && typeof r === 'object' && r.radio)
          // band code "1" == 2.4GHz; Nodes can't run mesh on 2.4GHz.
          .filter(r => isCap || String(r.band) !== '1')
          .map(r => r.radio);
      }
    } catch (_) {
      // Ignore — we'll fall through to "no choices available".
    }
    const used = new Set((treeMgmtView.trees || []).map(t => (t.tree_id || '').toLowerCase()));
    return radios.filter(r => !used.has(String(r).toLowerCase()));
  };

  const canAddTree = () => {
    if (!treeMgmtView) return false;
    const used = (treeMgmtView.trees || []).length;
    return used < maxAllowedTrees();
  };

  const openAddTreeDialog = () => {
    const candidates = availableTreeIDs();
    setAddTreeDraft({
      tree_id: candidates[0] || '',
      band: '5GHz',
      channel_mode: 'single',
      channel: '',
      channel_group_ids: [],
      channel_groups_5GHz: '',
      channel_groups_6GHz: '',
      htmode: '',
      tx_power_up: '',
      tx_power_down: '',
      max_children: '',
    });
    setAddTreeStatus({ type: '', message: '' });
    setAddTreeOpen(true);
  };

  const handleAddTreeSubmit = async () => {
    if (!addTreeDraft.tree_id) {
      setAddTreeStatus({ type: 'error', message: 'tree_id is required' });
      return;
    }
    if (!addTreeDraft.band) {
      setAddTreeStatus({ type: 'error', message: 'band is required' });
      return;
    }
    const body = { tree_id: addTreeDraft.tree_id, band: addTreeDraft.band };
    // Emit the channel selection that matches the chosen mode. The backend
    // rejects requests that set more than one of these fields, so we make
    // sure exactly one shows up.
    if (addTreeDraft.channel_mode === 'single' && addTreeDraft.channel !== '') {
      body.channel = parseInt(addTreeDraft.channel, 10);
    } else if (addTreeDraft.channel_mode === 'group_ids' && addTreeDraft.channel_group_ids.length > 0) {
      // Defensive: only emit IDs that match the chosen band. The UI already
      // filters them on band change but we re-check here in case the draft
      // got out of sync (e.g. directly mutated).
      body.channel_group_ids = addTreeDraft.channel_group_ids
        .filter(id => UNII_BY_ID[id]?.band === addTreeDraft.band);
    } else if (addTreeDraft.channel_mode === 'groups') {
      const groups = {};
      // Only the field for the selected band is enabled in the UI; ignore
      // the other one even if it has a residual value.
      if (addTreeDraft.band === '5GHz') {
        const five = parseIntList(addTreeDraft.channel_groups_5GHz);
        if (five === undefined) {
          setAddTreeStatus({ type: 'error', message: 'channel_groups[5GHz] must be a comma-separated list of integers' });
          return;
        }
        if (five.length) groups['5GHz'] = five;
      } else if (addTreeDraft.band === '6GHz') {
        const six = parseIntList(addTreeDraft.channel_groups_6GHz);
        if (six === undefined) {
          setAddTreeStatus({ type: 'error', message: 'channel_groups[6GHz] must be a comma-separated list of integers' });
          return;
        }
        if (six.length) groups['6GHz'] = six;
      }
      if (Object.keys(groups).length > 0) body.channel_groups = groups;
    }
    if (addTreeDraft.htmode) body.htmode = addTreeDraft.htmode;
    if (addTreeDraft.tx_power_up !== '') body.tx_power_up = parseInt(addTreeDraft.tx_power_up, 10);
    if (addTreeDraft.tx_power_down !== '') body.tx_power_down = parseInt(addTreeDraft.tx_power_down, 10);
    if (addTreeDraft.max_children !== '') body.max_children = parseInt(addTreeDraft.max_children, 10);
    try {
      const resp = await api.addTreeMgmtEntry(serial, body);
      if (resp && resp.view) setTreeMgmtView(resp.view);
      setAddTreeOpen(false);
      setSaveStatus({ type: 'success', message: `Added tree ${body.tree_id}` });
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 2000);
    } catch (err) {
      setAddTreeStatus({ type: 'error', message: err.message || 'Failed to add tree' });
    }
  };

  const handleOpenParentDialog = () => {
    console.log('Opening parent dialog with devices:', relations?.candidates || []);
    console.log('Current device serial:', serial);
    
    // Filter out only the current device
    const filteredDevices = (relations?.candidates || []).filter(d => {
      if (!d || !d.serial_number) {
        console.log('Skipping device with missing serial_number:', d);
        return false;
      }
      const isNotCurrentDevice = d.serial_number.toLowerCase() !== serial.toLowerCase();
      console.log(`Device ${d.serial_number}: isNotCurrentDevice=${isNotCurrentDevice}, connected=${d.connected}`);
      return isNotCurrentDevice;
    });
    
    console.log('Filtered devices for parent selection:', filteredDevices);
    setAvailableDevices(filteredDevices);
    setSelectedParent(device?.parent_serial || '');
    setParentDialogOpen(true);
  };

  const handleCloseParentDialog = () => {
    setParentDialogOpen(false);
    setSelectedParent('');
    setParentDeviceFilter('');
    setParentUpdateStatus({ type: '', message: '' });
  };

  const handleSetParent = async () => {
    try {
      await api.setParent(serial, selectedParent);
      setParentUpdateStatus({ type: 'success', message: 'Parent updated successfully' });
      // Update local device state instead of refreshing
      setDevice(prevDevice => ({
        ...prevDevice,
        parent_serial: selectedParent
      }));
      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseParentDialog();
      }, 1500);
    } catch (err) {
      setParentUpdateStatus({ 
        type: 'error', 
        message: err.response?.data?.message || err.message || 'Failed to update parent' 
      });
    }
  };

  const cancelConfigApply = () => {
    configApplyGenRef.current += 1;
    setApplyingConfig(false);
  };

  const closeConfigDialog = () => {
    cancelConfigApply();
    setConfigDialogOpen(false);
    setSaveStatus({ type: '', message: '' });
  };

  // Poll config-status until the device replies to our push (command_uuid
  // match), a newer/other change is applied (superseded), we time out, or
  // the apply generation is cancelled (dialog closed / serial changed).
  const pollConfigApplied = async (commandUuid, pushedAtMs, applyGen) => {
    const timeoutMs = 25000;
    const intervalMs = 1500;
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      if (configApplyGenRef.current !== applyGen) {
        return null; // cancelled
      }
      await new Promise((r) => setTimeout(r, intervalMs));
      if (configApplyGenRef.current !== applyGen) {
        return null;
      }
      let statusResp;
      try {
        statusResp = await api.getConfigStatus(serial);
      } catch (e) {
        continue; // transient; keep polling until deadline
      }
      if (configApplyGenRef.current !== applyGen) {
        return null;
      }
      const lcr = statusResp && statusResp.last_config_response;
      if (!lcr) {
        continue;
      }
      if (commandUuid && lcr.command_uuid === commandUuid) {
        return lcr.error === 0
          ? { type: 'success', message: 'Configuration applied by device' }
          : { type: 'error', message: `Device rejected configuration${lcr.text ? `: ${lcr.text}` : ''} (config still saved)` };
      }
      // A different command's response newer than our push: our push was
      // superseded by another change. Stop waiting.
      const receivedAtMs = lcr.received_at ? Date.parse(lcr.received_at) : 0;
      if (lcr.command_uuid && lcr.command_uuid !== commandUuid && receivedAtMs > pushedAtMs) {
        return { type: 'warning', message: 'Configuration saved; a newer change was applied to this device' };
      }
    }
    if (configApplyGenRef.current !== applyGen) {
      return null;
    }
    return { type: 'warning', message: 'Configuration saved, but the device did not acknowledge in time' };
  };

  const handleSaveConfig = async () => {
    let parsedConfig;
    try {
      parsedConfig = JSON.parse(configJson);
    } catch (e) {
      setSaveStatus({ type: 'error', message: 'Invalid JSON format' });
      return;
    }

    const pushedAtMs = Date.now();
    const applyGen = ++configApplyGenRef.current;
    try {
      const response = await api.configureDevice(serial, parsedConfig);
      if (configApplyGenRef.current !== applyGen) {
        return;
      }

      setOriginalConfig(configJson);
      const commandUuid = response.data && response.data.command_uuid;

      // Show progress while we wait for the device to acknowledge.
      setApplyingConfig(true);
      setSaveStatus({ type: 'info', message: 'Configuration saved. Applying on device…' });

      const result = await pollConfigApplied(commandUuid, pushedAtMs, applyGen);
      if (configApplyGenRef.current !== applyGen || !result) {
        return;
      }
      setApplyingConfig(false);
      setSaveStatus(result);

      // Keep failures/warnings visible longer than a clean success.
      const closeDelay = result.type === 'success' ? 1500 : 4000;
      setTimeout(() => {
        if (configApplyGenRef.current !== applyGen) {
          return;
        }
        setConfigDialogOpen(false);
        setSaveStatus({ type: '', message: '' });
      }, closeDelay);
    } catch (err) {
      if (configApplyGenRef.current !== applyGen) {
        return;
      }
      setApplyingConfig(false);
      const msg = err?.data?.error || err?.message || 'Failed to save configuration';
      // Backend returns 500 with this exact text when the device is offline
      // after the config was already persisted.
      if (msg === 'Failed to send command to device') {
        setSaveStatus({ type: 'warning', message: 'Configuration saved but not sent (device offline)' });
        setOriginalConfig(configJson);
        setTimeout(() => {
          if (configApplyGenRef.current !== applyGen) {
            return;
          }
          setConfigDialogOpen(false);
          setSaveStatus({ type: '', message: '' });
        }, 2000);
        return;
      }
      setSaveStatus({ type: 'error', message: msg });
    }
  };

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(configJson);
      const formatted = JSON.stringify(parsed, null, 2);
      setConfigJson(formatted);
      setIsJsonValid(true);
    } catch (e) {
      setIsJsonValid(false);
      setSaveStatus({ type: 'error', message: 'Invalid JSON format' });
    }
  };

  const handleReset = () => {
    setConfigJson(originalConfig);
    setIsJsonValid(true);
    setSaveStatus({ type: '', message: '' });
  };

  const handleEditorChange = (value) => {
    setConfigJson(value);
    try {
      JSON.parse(value);
      setIsJsonValid(true);
      setSaveStatus({ type: '', message: '' });
    } catch (e) {
      setIsJsonValid(false);
    }
  };

  const handleStartRtty = () => {
    // Simply open the RTTY page in a new tab
    window.open(`/rtty/${serial}`, '_blank');
  };



  const handleOpenAccessListDialog = async () => {
    const filteredDevices = (relations?.candidates || []).filter(d => {
      if (!d || !d.serial_number) {
        return false;
      }
      const isNotCurrentDevice = d.serial_number.toLowerCase() !== serial.toLowerCase();
      const isJnet2Compatible = d.compatible === 'jnet2';
      return isNotCurrentDevice && isJnet2Compatible;
    });
    setAvailableDevices(filteredDevices);

    try {
      const accessListData = await api.getAccessList(serial);
      const currentWhitelist = accessListData.whitelist ? accessListData.whitelist.split(',').filter(Boolean) : [];
      const currentBlacklist = accessListData.blacklist ? accessListData.blacklist.split(',').filter(Boolean) : [];
      setSelectedWhitelist(currentWhitelist);
      setSelectedBlacklist(currentBlacklist);
      const insist = accessListData.insist_time_sec ?? device?.tm_config?.whitelist_insist_time_sec;
      setAccessListInsistSec(insist !== undefined && insist !== null ? String(insist) : '');
    } catch (error) {
      console.error('Error fetching access list:', error);
      const tmConfig = device?.tm_config || {};
      const currentWhitelist = tmConfig.white_list ? tmConfig.white_list.split(',').filter(Boolean) : [];
      const currentBlacklist = tmConfig.black_list ? tmConfig.black_list.split(',').filter(Boolean) : [];
      setSelectedWhitelist(currentWhitelist);
      setSelectedBlacklist(currentBlacklist);
      const insist = tmConfig.whitelist_insist_time_sec;
      setAccessListInsistSec(insist !== undefined && insist !== null ? String(insist) : '');
    }
    setAccessListDialogOpen(true);
  };

  const handleCloseAccessListDialog = () => {
    setAccessListDialogOpen(false);
    setSelectedWhitelist([]);
    setSelectedBlacklist([]);
    setAccessListInsistSec('');
    setWhitelistDeviceFilter('');
    setBlacklistDeviceFilter('');
    setAccessListUpdateStatus({ type: '', message: '' });
  };

  const handleSetAccessList = async () => {
    const trimmedInsist = String(accessListInsistSec ?? '').trim();
    if (trimmedInsist !== '') {
      const insistNum = parseInt(trimmedInsist, 10);
      if (Number.isNaN(insistNum) || insistNum < 1 || insistNum > 600) {
        setAccessListUpdateStatus({
          type: 'error',
          message: 'Whitelist insist time must be between 1 and 600 seconds (or leave empty to keep current).',
        });
        return;
      }
    }

    try {
      const insistTimeSec = trimmedInsist !== '' ? parseInt(trimmedInsist, 10) : undefined;
      const accessResp = await api.setAccessList(serial, selectedWhitelist, selectedBlacklist, insistTimeSec);
      const offlineNote = Boolean(accessResp?.device_offline);
      setAccessListUpdateStatus({
        type: 'success',
        message: offlineNote
          ? (accessResp?.message || 'Configuration saved. Device is offline and will apply when connected.')
          : 'Access lists updated successfully',
      });
      
      // Update local device state with new tm_config
      setDevice(prevDevice => {
        const nextTm = {
          ...prevDevice.tm_config,
          white_list: selectedWhitelist.join(','),
          black_list: selectedBlacklist.join(','),
        };
        if (trimmedInsist !== '') {
          nextTm.whitelist_insist_time_sec = parseInt(trimmedInsist, 10);
        }
        return {
          ...prevDevice,
          tm_config: nextTm,
        };
      });
      
      // Refresh device data to get updated state
      setTimeout(() => {
        fetchDeviceData();
      }, 1000);
      
      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseAccessListDialog();
      }, 1500);
    } catch (err) {
      console.error('Error updating access lists:', err);
      setAccessListUpdateStatus({
        type: 'error',
        message: err.response?.data?.message || err.message || 'Failed to update access lists'
      });
    }
  };

  const handleWhitelistChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedWhitelist(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleBlacklistChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedBlacklist(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleOpenCommandDialog = () => {
    setCommandDialogOpen(true);
    setCommand('');
    setCommandStatus({ type: '', message: '' });
  };

  const handleCloseCommandDialog = () => {
    setCommandDialogOpen(false);
    setCommand('');
    setCommandStatus({ type: '', message: '' });
  };

  const handleSendCommand = async () => {
    try {
      await api.sendCommand(serial, command);
      setCommandStatus({ type: 'success', message: 'Command sent successfully' });
      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseCommandDialog();
      }, 1500);
    } catch (err) {
      setCommandStatus({ 
        type: 'error', 
        message: err.response?.data?.message || err.message || 'Failed to send command' 
      });
    }
  };

  const getDeviceIcon = () => {
    if (!device) return null;
    
    const model = device.capabilities?.model?.toLowerCase() || '';
    const compatible = device.compatible || device.capabilities?.compatible || '';
    const isAI = model.includes('nvidia') || model.includes('qrb5165-128gb');
    const isCAP = model.includes('cap');
    const isNode = model.includes('node');
    const isEdgeCore = compatible === 'edgecore_eap102' || model.includes('edgecore') || model.includes('eap102');

    if (isAI) {
      return <VideocamIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />;
    } else if (isEdgeCore) {
      return <RouterIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />;
    } else if (isCAP) {
      return <RouterIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />;
    } else if (isNode) {
      return <LightIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />;
    }
    return null;
  };

  const handleOpenConfigDialog = async () => {
    setConfigMode('fields');
    setFieldsSubTab('general');
    setSaveStatus({ type: '', message: '' });
    setConfigDialogOpen(true);
    // Fetch both views up-front so toggling between Fields and Raw is instant.
    await Promise.all([fetchTreeMgmtView(), fetchDeviceConfig()]);
  };

  const handleSwitchConfigMode = (mode) => {
    setConfigMode(mode);
    setSaveStatus({ type: '', message: '' });
  };

  const handleExportState = async () => {
    try {
      setIsLoading(true);
      console.log('Exporting device state for serial:', serial);
      const reportData = await api.getDeviceReport(serial);
      
      // Create a blob with the JSON data
      const jsonString = JSON.stringify(reportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Create a download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `device_report_${serial}_${new Date().toISOString().split('T')[0]}.json`;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('Device state exported successfully');
    } catch (error) {
      console.error('Error exporting device state:', error);
      // You could add a snackbar or alert here to show the error to the user
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Device Details
          </Typography>
          {(userInfo || environment) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
              {userInfo && userInfo.name && (
                <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {userInfo.name}
                </Typography>
              )}
              {environment && (
                <Typography
                  variant="body2"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    textTransform: 'capitalize',
                    opacity: 0.9
                  }}
                >
                  ({environment})
                </Typography>
              )}
            </Box>
          )}
          <IconButton
            color="inherit"
            onClick={handleLogoutClick}
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <LogoutIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, width: '100%' }}>
        <Box sx={{ maxWidth: 1200, margin: '20px auto', padding: 2 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Back
          </Button>

        <Box sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: 2, py: 1.5, borderBottom: '3px solid', borderColor: 'text.primary' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              aria-label={device?.connected ? 'Connected' : 'Offline'}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: device?.connected ? 'success.main' : 'error.main',
              }}
            />
            <Typography
              variant="h5"
              component="h1"
              sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1.5rem', letterSpacing: '-0.02em', lineHeight: 1.2 }}
            >
              {serial}
            </Typography>
            {device?.capabilities?.model && (() => {
              const model = device.capabilities.model.toLowerCase();
              const compatible = device.compatible || device.capabilities?.compatible || '';
              let label = 'Node';
              let bgcolor = 'warning.lighter';
              let color = 'warning.dark';
              if (compatible === 'edgecore_eap102' || model.includes('edgecore') || model.includes('eap102')) {
                label = 'EdgeCore';
              } else if (model.includes('nvidia') || model.includes('qrb5165-128gb')) {
                label = 'AI';
                bgcolor = 'primary.lighter';
                color = 'primary.dark';
              } else if (model.includes('cap')) {
                label = 'CAP';
                bgcolor = 'info.lighter';
                color = 'info.dark';
              }
              return (
                <Typography
                  component="span"
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor,
                    color,
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    letterSpacing: '0.04em',
                    lineHeight: 1.6,
                  }}
                >
                  {label}
                </Typography>
              );
            })()}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {canWriteBackhaul && device?.compatible === 'jnet2' && (
              <>
                <Tooltip title="Manage Access Lists">
                  <IconButton
                    onClick={handleOpenAccessListDialog}
                    size="small"
                    sx={{ 
                      width: '28px',
                      height: '28px',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <AccessListIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Set Parent">
                  <IconButton
                    onClick={handleOpenParentDialog}
                    size="small"
                    sx={{ 
                      width: '28px',
                      height: '28px',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <LinkIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export State">
                  <IconButton
                    onClick={handleExportState}
                    size="small"
                    disabled={isLoading}
                    sx={{ 
                      width: '28px',
                      height: '28px',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      },
                      '&.Mui-disabled': {
                        borderColor: 'divider',
                        backgroundColor: 'action.disabledBackground'
                      }
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </Tooltip>
                <DeviceReportMenuButton serial={device?.serial_number || serial} />
              </>
            )}
            {canWriteBackhaul && (
            <>
            <Tooltip title="Command">
              <IconButton
                onClick={handleOpenCommandDialog}
                size="small"
                sx={{ 
                  width: '28px',
                  height: '28px',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <CommandIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="RTTY">
              <IconButton
                onClick={handleStartRtty}
                size="small"
                sx={{ 
                  width: '28px',
                  height: '28px',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <RttyIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Configure">
              <IconButton
                onClick={handleOpenConfigDialog}
                size="small"
                sx={{ 
                  width: '28px',
                  height: '28px',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ConfigureIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
            {isLastKnownGoodEligible(device) && (
            <LastKnownGoodMenu
              scope="device"
              serial={serial}
              buttonSx={{
                width: '28px',
                height: '28px',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
              onComplete={({ type, message }) => {
                setLkgStatus({ type, message });
                if (type === 'success') {
                  fetchDeviceData();
                  fetchDeviceConfig();
                  setTimeout(() => setLkgStatus({ type: '', message: '' }), 5000);
                }
              }}
            />
            )}
            <Tooltip title="Show on map">
              <IconButton
                onClick={handleShowOnMap}
                size="small"
                sx={{
                  width: '28px',
                  height: '28px',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <MapIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
            </>
            )}
          </Box>
        </Box>

        {lkgStatus.message && (
          <Alert severity={lkgStatus.type || 'info'} sx={{ mx: 2, mb: 1.25 }}>
            {lkgStatus.message}
          </Alert>
        )}
        </Box>

        <Box sx={{ mb: 2 }}>
        <Tabs
          value={detailTabs.some((tab) => tab.id === detailTab) ? detailTab : 'info'}
          onChange={(_, value) => setDetailTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 42,
            overflow: 'visible',
            '& .MuiTabs-scroller': { overflow: 'visible !important' },
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: 0.5 },
            '& .MuiTab-root': {
              minHeight: 42,
              py: 0.5,
              px: 2,
              textTransform: 'none',
              color: 'text.secondary',
              border: '1px solid transparent',
              borderBottom: 'none',
              borderRadius: '8px 8px 0 0',
              zIndex: 1,
            },
            '& .Mui-selected': {
              color: 'text.primary',
              fontWeight: 700,
              bgcolor: 'background.paper',
              borderColor: 'divider',
              borderBottom: '1px solid',
              borderBottomColor: 'background.paper',
              mb: '-1px',
            },
          }}
        >
          {detailTabs.map((tab) => (
            <Tab key={tab.id} value={tab.id} label={tab.label} />
          ))}
        </Tabs>
        <Box sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          '& > .MuiPaper-root': { boxShadow: 'none', mb: 0 },
          '& > .MuiGrid-root > .MuiGrid-item > .MuiPaper-root': { boxShadow: 'none', mb: 0 },
        }}>

        {detailTab === 'commands' && (
          <CommandHistory serial={device.serial_number} />
        )}

        {detailTab === 'info' && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {getDeviceIcon()}
                <Typography variant="h6">Device Information</Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Serial Number:</Typography>
                    <Chip
                      size="small"
                      label={device.serial_number}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>IP Address:</Typography>
                    <Chip
                      size="small"
                      label={getPeerAddress(device.peer_address) || 'N/A'}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  {device.cap_serial && device.cap_serial.toLowerCase() !== device.serial_number.toLowerCase() && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>CAP Serial:</Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => navigate(`/device/${device.cap_serial.toLowerCase()}`)}
                        sx={{ 
                          textTransform: 'none', 
                          p: 0, 
                          minWidth: 0,
                          justifyContent: 'flex-start'
                        }}
                      >
                        <Chip
                          size="small"
                          label={device.cap_serial}
                          sx={{ 
                            height: 24,
                            fontSize: '0.75rem',
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      </Button>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Uptime:</Typography>
                    <Chip
                      size="small"
                      label={device.uptime ? (() => {
                        const days = Math.floor(device.uptime / 86400);
                        const hours = Math.floor((device.uptime % 86400) / 3600);
                        const minutes = Math.floor((device.uptime % 3600) / 60);
                        
                        if (days > 0) {
                          return days >= 7 ? `${Math.floor(days / 7)}w ${days % 7}d` : `${days}d ${hours}h`;
                        } else if (hours > 0) {
                          return `${hours}h ${minutes}m`;
                        } else {
                          return `${minutes}m`;
                        }
                      })() : 'N/A'}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Last Seen:</Typography>
                    <Chip
                      size="small"
                      label={device.last_seen ? new Date(device.last_seen).toLocaleString() : 'N/A'}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  {!(device.capabilities?.model || '').toLowerCase().includes('cap') && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Connected Time:</Typography>
                    <Chip
                      size="small"
                      label={(() => {
                        // Get connected time from parent's WiFi associations
                        const getConnectedTime = () => {
                          if (!meshParent || !device.parent_serial) return null;
                          const parent = meshParent;
                          
                          if (!parent || (!parent.private_wifi?.interfaces && !parent.private_wifi?.Interfaces)) return null;
                          
                          // Build device identifiers: MACs from radios AND serial number
                          // jnet2 uses serial number in associations, EdgeCore uses MAC
                          const deviceMacs = device.radios?.map(radio => radio.mac?.toLowerCase()).filter(Boolean) || [];
                          const deviceSerial = device.serial_number?.toLowerCase();
                          const deviceIdentifiers = new Set(deviceMacs);
                          if (deviceSerial) {
                            deviceIdentifiers.add(deviceSerial);
                            deviceIdentifiers.add(deviceSerial.replace(/:/g, ''));
                          }
                          if (deviceIdentifiers.size === 0) return null;
                          
                          // Look for this device in parent's associations
                          const parentIfaces = parent.private_wifi?.interfaces || parent.private_wifi?.Interfaces;
                          for (const iface of parentIfaces || []) {
                            // Handle both Associations/associations for compatibility
                            const associations = iface.Associations || iface.associations;
                            if (associations) {
                              for (const assoc of associations) {
                                // Handle both Station/station and ConnectedTime/connected_time
                                const station = assoc.Station || assoc.station;
                                const connectedTime = assoc.ConnectedTime || assoc.connected_time;
                                const stationId = station?.toLowerCase().replace(/:/g, '');
                                if ((deviceIdentifiers.has(stationId) || deviceIdentifiers.has(station?.toLowerCase())) && connectedTime) {
                                  return connectedTime;
                                }
                              }
                            }
                          }
                          return null;
                        };
                        
                        const connectedTime = getConnectedTime();
                        if (!connectedTime) return 'N/A';
                        
                        // Format connected time similar to uptime
                        const days = Math.floor(connectedTime / 86400);
                        const hours = Math.floor((connectedTime % 86400) / 3600);
                        const minutes = Math.floor((connectedTime % 3600) / 60);
                        
                        if (days > 0) {
                          return days >= 7 ? `${Math.floor(days / 7)}w ${days % 7}d` : `${days}d ${hours}h`;
                        } else if (hours > 0) {
                          return `${hours}h ${minutes}m`;
                        } else {
                          return `${minutes}m`;
                        }
                      })()}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  )}
                  <InfoSegmentChips device={device} segmentNames={segmentNames} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {(() => {
                    const offline = device.connected === false;
                    const shownParent = offline
                      ? (device.last_known_parent_serial || device.parent_serial)
                      : device.parent_serial;
                    if (!shownParent || shownParent === 'N/A') return null;
                    return (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ minWidth: '160px', fontWeight: 'bold' }}>
                        {offline ? 'Last known parent:' : 'Parent Serial:'}
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => navigate(`/device/${shownParent.toLowerCase()}`)}
                        sx={{ 
                          textTransform: 'none', 
                          p: 0, 
                          minWidth: 0,
                          justifyContent: 'flex-start'
                        }}
                      >
                        <Chip
                          size="small"
                          label={shownParent}
                          sx={{ 
                            height: 24,
                            fontSize: '0.75rem',
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      </Button>
                    </Box>
                    );
                  })()}
                  {deviceAiBoardMac(device) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>AI Device MAC:</Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => navigate(`/device/${deviceAiBoardMac(device).toLowerCase()}`)}
                        sx={{ textTransform: 'none', p: 0, minWidth: 0, justifyContent: 'flex-start' }}
                      >
                        <Chip
                          size="small"
                          label={deviceAiBoardMac(device)}
                          sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            fontFamily: 'monospace',
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </Button>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Firmware:</Typography>
                    <Chip
                      size="small"
                      label={device.firmware || 'N/A'}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>Compatible:</Typography>
                    <Chip
                      size="small"
                      label={device.compatible || 'N/A'}
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  {(device.backhaul_tx_ratio !== undefined && device.backhaul_tx_ratio !== null) ||
                   (device.backhaul_tx_failed_percentage !== undefined && device.backhaul_tx_failed_percentage !== null) ||
                   (getParentLatencyMs(device) !== null) ||
                   (getCapLatencyMs(device) !== null) ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Backhaul Health:</Typography>
                      
                      {/* TX Ratio */}
                      {device.backhaul_tx_ratio !== undefined && device.backhaul_tx_ratio !== null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, ml: 2 }}>
                          <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary', fontWeight: 'bold' }}>TX Ratio:</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Transmission success ratio - percentage of packets successfully transmitted over the backhaul link. Higher values indicate better link quality.">
                              <Chip
                                size="small"
                                label={`${(device.backhaul_tx_ratio * 100).toFixed(1)}%`}
                                color={
                                  device.backhaul_tx_ratio >= 0.8 ? 'success' :
                                  device.backhaul_tx_ratio >= 0.6 ? 'warning' : 'error'
                                }
                                sx={{ 
                                  height: 24,
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  cursor: 'help',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            </Tooltip>
                            <Chip
                              size="small"
                              label={
                                device.backhaul_tx_ratio >= 0.8 ? 'Good' :
                                device.backhaul_tx_ratio >= 0.6 ? 'Fair' : 'Poor'
                              }
                              variant="outlined"
                              color={
                                device.backhaul_tx_ratio >= 0.8 ? 'success' :
                                device.backhaul_tx_ratio >= 0.6 ? 'warning' : 'error'
                              }
                              sx={{ 
                                height: 24,
                                fontSize: '0.75rem',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                      
                      {/* TX Failed Percentage */}
                      {device.backhaul_tx_failed_percentage !== undefined && device.backhaul_tx_failed_percentage !== null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, ml: 2 }}>
                          <Typography variant="body2" sx={{ minWidth: '140px', color: 'text.secondary', fontWeight: 'bold' }}>TX Failed:</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Transmission failure percentage - percentage of packets that failed to transmit over the backhaul link. Lower values indicate better link quality.">
                              <Chip
                                size="small"
                                label={`${(device.backhaul_tx_failed_percentage * 100).toFixed(1)}%`}
                                color={
                                  device.backhaul_tx_failed_percentage <= 0.1 ? 'success' :
                                  device.backhaul_tx_failed_percentage <= 0.3 ? 'warning' : 'error'
                                }
                                sx={{ 
                                  height: 24,
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  cursor: 'help',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            </Tooltip>
                            <Chip
                              size="small"
                              label={
                                device.backhaul_tx_failed_percentage <= 0.1 ? 'Good' :
                                device.backhaul_tx_failed_percentage <= 0.3 ? 'Fair' : 'Poor'
                              }
                              variant="outlined"
                              color={
                                device.backhaul_tx_failed_percentage <= 0.1 ? 'success' :
                                device.backhaul_tx_failed_percentage <= 0.3 ? 'warning' : 'error'
                              }
                              sx={{ 
                                height: 24,
                                fontSize: '0.75rem',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                      
                      {/* Latency: median round-trip to parent / CAP, with jitter
                          (max-min) carried in the tooltip. Median is more
                          representative of typical RTT than the average for
                          spiky wireless links. */}
                      {(() => {
                        const rows = [
                          { label: 'Latency to Parent:', summary: device.latency?.to_parent, tooltipLabel: 'Median round-trip to immediate parent' },
                          { label: 'Latency to CAP:', summary: device.latency?.to_cap, tooltipLabel: 'Median round-trip to CAP (root)' },
                        ];
                        return rows.map(({ label, summary, tooltipLabel }) => {
                          const ms = getLatencyValue(summary);
                          if (ms === null) return null;
                          const chipColor = getLatencyChipColor(ms);
                          const qualLabel = getLatencyLabel(ms);
                          return (
                            <Box key={label} sx={{ display: 'flex', alignItems: 'center', mb: 1, ml: 2 }}>
                              <Typography variant="body2" sx={{ minWidth: '140px', color: 'text.secondary', fontWeight: 'bold' }}>{label}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title={formatLatencyTooltip(summary, tooltipLabel)}>
                                  <Chip
                                    size="small"
                                    label={formatLatencyMs(ms)}
                                    color={chipColor}
                                    sx={{
                                      height: 24,
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold',
                                      cursor: 'help',
                                      '& .MuiChip-label': { px: 1 }
                                    }}
                                  />
                                </Tooltip>
                                <Chip
                                  size="small"
                                  label={qualLabel}
                                  variant="outlined"
                                  color={chipColor}
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    '& .MuiChip-label': { px: 1 }
                                  }}
                                />
                                {summary?.jitter_ms !== undefined && summary?.jitter_ms !== null && (
                                  <Tooltip title={`Jitter (max - min) over ${summary.samples ?? 0} samples`}>
                                    <Chip
                                      size="small"
                                      label={`±${formatLatencyMs(summary.jitter_ms)}`}
                                      variant="outlined"
                                      sx={{
                                        height: 24,
                                        fontSize: '0.7rem',
                                        color: 'text.secondary',
                                        cursor: 'help',
                                        '& .MuiChip-label': { px: 1 }
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          );
                        });
                      })()}
                    </Box>
                  ) : null}
                  {device.parent_serial && (() => {
                    const parent = meshParent;
                    const connectionDetails = getConnectionDetails(device, parent);
                    if (!connectionDetails?.rssi && !connectionDetails?.stationRssi) return null;
                    
                    return (
                      <Box sx={{ mt: 2 }}>
                        {connectionDetails.rssi && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>STA → AP RSSI:</Typography>
                            <Chip
                              size="small"
                              label={`${connectionDetails.rssi}dBm`}
                              sx={{ 
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          </Box>
                        )}
                        {connectionDetails.stationRssi && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ minWidth: '140px', fontWeight: 'bold' }}>AP → STA RSSI:</Typography>
                            <Chip
                              size="small"
                              label={`${connectionDetails.stationRssi}dBm`}
                              sx={{ 
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
        )}

        {/* Data Counters */}
        {detailTab === 'counters' && showDataCountersSection && deviceDataCounters && (
          <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
            <Tooltip
              title="Cumulative counters since device power up. Rates are averaged over the last 2 minutes"
              placement="top"
            >
              <Typography variant="h6" gutterBottom sx={{ cursor: 'help', display: 'inline-block' }}>
                Data Counters
              </Typography>
            </Tooltip>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={deviceDataCounters.kind === 'node' ? 3 : 4}>
                <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Total
                  </Typography>
                  {renderDataCounterTraffic(deviceDataCounters.total, dataCounterRates, 'total')}
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={deviceDataCounters.kind === 'node' ? 3 : 4}>
                <Paper elevation={1} sx={{ p: 2, height: '100%', borderLeft: '4px solid', borderColor: 'secondary.main' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Public WiFi
                  </Typography>
                  {renderDataCounterTraffic(deviceDataCounters.breakdown.public, dataCounterRates, 'public_wifi')}
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={deviceDataCounters.kind === 'node' ? 3 : 4}>
                <Paper elevation={1} sx={{ p: 2, height: '100%', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Children
                  </Typography>
                  {renderDataCounterTraffic(deviceDataCounters.breakdown.children, dataCounterRates, 'children')}
                </Paper>
              </Grid>
              {deviceDataCounters.kind === 'node' && (
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={1} sx={{ p: 2, height: '100%', borderLeft: '4px solid', borderColor: 'info.main' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Ethernet
                    </Typography>
                    {renderDataCounterTraffic(deviceDataCounters.ethernet, dataCounterRates, 'ethernet')}
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Operator notes */}
        {detailTab === 'notes' && (
        <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <StickyNote2Icon sx={{ color: deviceHasNotes(device) ? MAP_UI.notesBadge : 'text.disabled' }} />
            <Typography variant="h6">Notes</Typography>
          </Box>
          <DeviceNotesEditor
            device={device}
            minRows={3}
            maxRows={12}
            onSaved={(fields) => setDevice((prev) => (prev ? { ...prev, ...fields } : prev))}
          />
        </Paper>
        )}

        {/* Provisioning Section */}
        {detailTab === 'provisioning' && device.provisioning && (() => {
          const prov = device.provisioning;
          const aiMac = prov.AiDeviceMac || prov.ai_device_mac;
          const jnet1Embedded = prov.Jnet1EmbeddedMac || prov.jnet1_embedded_mac;
          const dimmerEmbedded = prov.DimmerEmbeddedSerial || prov.dimmer_embedded_serial;
          const jnet1Nema = prov.Jnet1NemaMac || prov.jnet1_nema_mac;
          const dimmerNema = prov.DimmerNemaSerial || prov.dimmer_nema_serial;
          const fixtureSerial = prov.FixtureSerial || prov.fixture_serial;

          if (!aiMac && !jnet1Embedded && !dimmerEmbedded && !jnet1Nema && !dimmerNema && !fixtureSerial) {
            return null;
          }

          // Helper function to format jnet1 values: show both decimal and hex
          const formatJnet1 = (value) => {
            if (!value) return value;
            // Check if value is numeric (can be string or number)
            const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
            if (!isNaN(numValue) && isFinite(numValue)) {
              // Convert to hex (uppercase) and pad to even length
              let hex = numValue.toString(16).toUpperCase();
              // Pad with leading zero if odd length
              if (hex.length % 2 !== 0) {
                hex = '0' + hex;
              }
              // Add colons between every 2 digits
              const hexFormatted = hex.match(/.{2}/g).join(':');
              // Return both decimal and hex: "decimal (hex)"
              return `${value} (${hexFormatted})`;
            }
            // If not numeric, return as-is
            return value;
          };

          const row = (label, value) => (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 110 }}>
                {label}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                {value}
              </Typography>
            </Box>
          );

          return (
            <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Provisioning</Typography>
              <Stack spacing={0.5}>
                {aiMac && row('ai', aiMac)}
                {jnet1Embedded && row('jnet1', formatJnet1(jnet1Embedded))}
                {dimmerEmbedded && row('dimmer', dimmerEmbedded)}
                {jnet1Nema && row('jnet1 (nema)', formatJnet1(jnet1Nema))}
                {dimmerNema && row('dimmer (nema)', dimmerNema)}
                {fixtureSerial && row('fixture', fixtureSerial)}
              </Stack>
            </Paper>
          );
        })()}

        {/* Connected AI Device - skip for EdgeCore devices and AI devices */}
        {detailTab === 'ai' && resolvedAIDevices.length > 0 && !(device?.compatible || '').toLowerCase().includes('edgecore') && !(() => {
          const model = device?.capabilities?.model?.toLowerCase() || '';
          return model.includes('nvidia') || model.includes('qrb5165-128gb');
        })() && (
          <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Typography variant="h6">Connected AI Device</Typography>
              {canWriteBackhaul && <RebootAiBoardButton device={device} />}
            </Box>
            <Grid container spacing={2}>
              {resolvedAIDevices.map((aiDevice) => (
                <Grid item xs={12} key={aiDevice.serial_number}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => navigate(`/device/${aiDevice.serial_number.toLowerCase()}`)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Tooltip title={aiDevice.connected ? 'Connected' : 'Disconnected'}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: aiDevice.connected ? '#4caf50' : '#f44336',
                            marginRight: 2,
                          }}
                        />
                      </Tooltip>
                      <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                        {aiDevice.name || aiDevice.serial_number}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Chip
                          size="small"
                          label={`Firmware: ${aiDevice.firmware || 'N/A'}`}
                          sx={{ 
                            height: 24,
                            fontSize: '0.75rem',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                        <Chip
                          size="small"
                          label={`Last Seen: ${aiDevice.last_seen ? new Date(aiDevice.last_seen).toLocaleString() : 'N/A'}`}
                          sx={{ 
                            height: 24,
                            fontSize: '0.75rem',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Connected Jnet2 Children Section */}
        {detailTab === 'children' && childJnet2Devices.length > 0 && (
          <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Tooltip
                title="Devices matched from this AP's WiFi associations (same source as the radio interface count chips)"
                placement="top"
              >
                <Typography variant="h6" sx={{ cursor: 'help' }}>Associated Children</Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  size="small"
                  label={`Total: ${childJnet2Devices.length}`}
                  color="default"
                />
                <Chip
                  size="small"
                  label={`Connected: ${childJnet2Devices.filter(c => c.connected).length}`}
                  color="success"
                />
                <Tooltip title="All AP-side associations on this device (includes non-jnet2 clients; see radio interface chips)" placement="top">
                  <Chip
                    size="small"
                    label={`AP associations: ${countActiveAssociations(
                      device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces,
                    )}`}
                    color="primary"
                    sx={{ cursor: 'help' }}
                  />
                </Tooltip>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
              Shown: jnet2 devices matched from this AP&apos;s associations • Connected: matched children currently online • AP associations: all stations on AP interfaces (may include non-jnet2 clients)
            </Typography>
            <Grid container spacing={2}>
              {childJnet2Devices.map((childDevice) => {
                const connectionDetails = getUplinkDetails(childDevice, device);
                
                return (
                  <Grid item xs={12} key={childDevice.serial_number}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer'
                        },
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1
                      }}
                      onClick={() => navigate(`/device/${childDevice.serial_number.toLowerCase()}`)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title={childDevice.connected ? 'Connected' : 'Disconnected'}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: childDevice.connected ? '#4caf50' : '#f44336',
                                marginRight: 2,
                              }}
                            />
                          </Tooltip>
                          <Typography variant="subtitle1">
                            {childDevice.name || childDevice.serial_number}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={`Firmware: ${childDevice.firmware || 'N/A'}`}
                            sx={{ 
                              height: 24,
                              fontSize: '0.75rem',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                          <Chip
                            size="small"
                            label={`Last Seen: ${childDevice.last_seen ? new Date(childDevice.last_seen).toLocaleString() : 'N/A'}`}
                            sx={{ 
                              height: 24,
                              fontSize: '0.75rem',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        </Box>
                      </Box>
                      {connectionDetails ? (
                        <Box sx={{ 
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 2,
                          mb: 2
                        }}>
                        {/* RX Information */}
                        <Tooltip title={
                          connectionDetails?.source === 'uplink'
                            ? `Child STA-mode radio RX histograms (parent → child). Estimated PHY rate is calculated from dominant MCS/NSS/BW${connectionDetails.rxRate?.MCS ? ` (MCS ${connectionDetails.rxRate.MCS}` : ''}${connectionDetails.rxRate?.NSS ? `, NSS ${connectionDetails.rxRate.NSS}` : ''}${connectionDetails.rxRate?.BW ? `, BW ${connectionDetails.rxRate.BW} MHz` : ''}${connectionDetails.rxRate?.MCS ? ')' : ''}.${connectionDetails.rxTotalBytes ? ` ${(connectionDetails.rxTotalBytes / 1e6).toFixed(1)} MB sampled.` : ''} Same source as optimizer.`
                            : 'As measured by Station (parent-side association — MCS/NSS/BW are aliased across all associated stations)'
                        }>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: 'background.default',
                            p: 1,
                            borderRadius: 1,
                            gap: 1
                          }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                              {childLinkCardTitle('child', connectionDetails?.source)}
                            </Typography>
                            <Box sx={{ 
                              bgcolor: 'background.paper',
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              overflow: 'hidden'
                            }}>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>RSSI</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>Est. Rate</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>NSS</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>MCS</Typography>
                              </Box>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.stationRssi ? `${connectionDetails.stationRssi}dBm` : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {(() => {
                                    const rate = connectionDetails.rxRate?.Bitrate || connectionDetails.rxRate?.bitrate;
                                    return rate ? `${(rate / 1000000).toFixed(2)} Mbps` : 'N/A';
                                  })()}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rxRate?.NSS || connectionDetails.rxRate?.nss || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rxRate?.MCS || connectionDetails.rxRate?.mcs || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>

                        {/* TX Information */}
                        <Tooltip title={
                          connectionDetails?.source === 'uplink'
                            ? `Child STA-mode radio TX histograms (child → parent). Estimated PHY rate is calculated from dominant MCS/NSS/BW${connectionDetails.txRate?.MCS ? ` (MCS ${connectionDetails.txRate.MCS}` : ''}${connectionDetails.txRate?.NSS ? `, NSS ${connectionDetails.txRate.NSS}` : ''}${connectionDetails.txRate?.BW ? `, BW ${connectionDetails.txRate.BW} MHz` : ''}${connectionDetails.txRate?.MCS ? ')' : ''}.${connectionDetails.txTotalBytes ? ` ${(connectionDetails.txTotalBytes / 1e6).toFixed(1)} MB sampled.` : ''} Same source as optimizer.`
                            : 'As measured by Parent AP (parent-side association — MCS/NSS/BW are aliased across all associated stations)'
                        }>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: 'background.default',
                            p: 1,
                            borderRadius: 1,
                            gap: 1
                          }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                              {childLinkCardTitle('ap', connectionDetails?.source)}
                            </Typography>
                            <Box sx={{ 
                              bgcolor: 'background.paper',
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              overflow: 'hidden'
                            }}>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>RSSI</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>Est. Rate</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>NSS</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>MCS</Typography>
                              </Box>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rssi ? `${connectionDetails.rssi}dBm` : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {(() => {
                                    const rate = connectionDetails.txRate?.Bitrate || connectionDetails.txRate?.bitrate;
                                    return rate ? `${(rate / 1000000).toFixed(2)} Mbps` : 'N/A';
                                  })()}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.txRate?.NSS || connectionDetails.txRate?.nss || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.txRate?.MCS || connectionDetails.txRate?.mcs || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>
                      </Box>
                      ) : (
                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          p: 2,
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          mb: 2
                        }}>
                          <Typography variant="body2" color="text.secondary">
                            No connection details available
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}

        {/* Connected EdgeCore Children Section (from associations on this AP) - ONLY for EdgeCore devices */}
        {detailTab === 'edgecore-children' && childEdgeCoreDevices.length > 0 && device?.compatible?.toLowerCase()?.includes('edgecore') && (
          <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Tooltip title="Based on associations reported by this EdgeCore AP" placement="top">
                <Typography variant="h6" sx={{ cursor: 'help' }}>Direct Children (EdgeCore)</Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  size="small"
                  label={`Total: ${childEdgeCoreDevices.length}`}
                  color="default"
                />
                <Chip
                  size="small"
                  label={`Connected: ${childEdgeCoreDevices.length}`}
                  color="success"
                />
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
              EdgeCore children are always connected when present in associations
                        </Typography>
            <Grid container spacing={2}>
              {childEdgeCoreDevices.map((childDevice) => {
                // For EdgeCore children, use association data directly
                const assoc = childDevice.edgeCoreAssoc;
                
                // Format connected time from seconds
                const formatConnectedTime = (seconds) => {
                  if (!seconds || seconds <= 0) return null;
                  if (seconds < 60) return `${seconds}s`;
                  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
                  const hours = Math.floor(seconds / 3600);
                  const mins = Math.floor((seconds % 3600) / 60);
                  if (hours >= 24) {
                    const days = Math.floor(hours / 24);
                    return `${days}d ${hours % 24}h`;
                  }
                  return `${hours}h ${mins}m`;
                };

                // Format bytes
                const formatBytes = (bytes) => {
                  if (!bytes || bytes <= 0) return '0 KB';
                  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
                  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                };

                return (
                  <Grid item xs={12} sm={6} md={4} key={childDevice.serial_number}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 1.5,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer'
                        },
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                      }}
                      onClick={() => {
                        const targetSerial = childDevice.serial_number?.toLowerCase();
                        if (targetSerial) {
                          navigate(`/device/${targetSerial}`);
                        }
                      }}
                    >
                      {/* Header with status and serial */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Tooltip title="Connected">
                        <Box
                          sx={{
                              width: 10,
                              height: 10,
                            borderRadius: '50%',
                              backgroundColor: '#4caf50',
                              mr: 1,
                              flexShrink: 0
                          }}
                        />
                        </Tooltip>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                          {childDevice.serial_number}
                        </Typography>
                    </Box>
                    
                      {/* Info chips */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {assoc?.rssi && (
                          <Tooltip title="Signal strength as seen by the EdgeCore AP">
                            <Chip
                              size="small"
                              label={`AP RSSI: ${assoc.rssi} dBm`}
                              sx={{ height: 22, fontSize: '0.7rem' }}
                              color={parseInt(assoc.rssi) >= -70 ? 'success' : parseInt(assoc.rssi) >= -80 ? 'warning' : 'error'}
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {formatConnectedTime(assoc?.connectedTime) && (
                          <Tooltip title="Time this device has been connected to the AP">
                            <Chip
                              size="small"
                              label={formatConnectedTime(assoc.connectedTime)}
                              sx={{ height: 22, fontSize: '0.7rem' }}
                              color="info"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                        {assoc && (assoc.txBytes > 0 || assoc.rxBytes > 0) && (
                          <Chip
                            size="small"
                            label={`↑${formatBytes(assoc.txBytes)} ↓${formatBytes(assoc.rxBytes)}`}
                            sx={{ height: 22, fontSize: '0.7rem' }}
                            color="default"
                            variant="outlined"
                          />
                        )}
                      </Box>
                  </Paper>
                </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}


        {/* Parent Network Device Section for AI devices and EdgeCore devices */}
        {detailTab === 'parent' && parentDevice && (
          (() => {
            const modelLower = (device?.capabilities?.model || '').toString().toLowerCase();
            return modelLower.includes('nvidia') || modelLower.includes('qrb5165-128gb');
          })() ||
          device?.compatible === 'edgecore_eap102' ||
          device?.capabilities?.compatible === 'edgecore_eap102'
        ) && (
          <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Parent Network Device</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 2,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      cursor: 'pointer'
                    },
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1
                  }}
                  onClick={() => navigate(`/device/${parentDevice.serial_number.toLowerCase()}`)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title={parentDevice.connected ? 'Connected' : 'Disconnected'}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: parentDevice.connected ? '#4caf50' : '#f44336',
                            marginRight: 2,
                          }}
                        />
                      </Tooltip>
                      <Typography variant="subtitle1">
                        {parentDevice.name || parentDevice.serial_number}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={`Firmware: ${parentDevice.firmware || 'N/A'}`}
                        sx={{ 
                          height: 24,
                          fontSize: '0.75rem',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                      <Chip
                        size="small"
                        label={`Last Seen: ${parentDevice.last_seen ? new Date(parentDevice.last_seen).toLocaleString() : 'N/A'}`}
                        sx={{ 
                          height: 24,
                          fontSize: '0.75rem',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  {(() => {
                    const connectionDetails = getUplinkDetails(device, parentDevice);
                    if (!connectionDetails) return null;
                    
                    return (
                      <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 2,
                        mb: 2
                      }}>
                        {/* RX Information */}
                        <Tooltip title={
                          connectionDetails?.source === 'uplink'
                            ? `Child STA-mode radio RX histograms (parent → child). Estimated PHY rate is calculated from dominant MCS/NSS/BW${connectionDetails.rxRate?.MCS ? ` (MCS ${connectionDetails.rxRate.MCS}` : ''}${connectionDetails.rxRate?.NSS ? `, NSS ${connectionDetails.rxRate.NSS}` : ''}${connectionDetails.rxRate?.BW ? `, BW ${connectionDetails.rxRate.BW} MHz` : ''}${connectionDetails.rxRate?.MCS ? ')' : ''}.${connectionDetails.rxTotalBytes ? ` ${(connectionDetails.rxTotalBytes / 1e6).toFixed(1)} MB sampled.` : ''} Same source as optimizer.`
                            : 'As measured by Station (parent-side association — MCS/NSS/BW are aliased across all associated stations)'
                        }>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: 'background.default',
                            p: 1,
                            borderRadius: 1,
                            gap: 1
                          }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                              {childLinkCardTitle('child', connectionDetails?.source)}
                            </Typography>
                            <Box sx={{ 
                              bgcolor: 'background.paper',
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              overflow: 'hidden'
                            }}>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>RSSI</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>Est. Rate</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>NSS</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>MCS</Typography>
                              </Box>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.stationRssi ? `${connectionDetails.stationRssi}dBm` : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {(() => {
                                    const rate = connectionDetails.rxRate?.Bitrate || connectionDetails.rxRate?.bitrate;
                                    return rate ? `${(rate / 1000000).toFixed(2)} Mbps` : 'N/A';
                                  })()}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rxRate?.NSS || connectionDetails.rxRate?.nss || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rxRate?.MCS || connectionDetails.rxRate?.mcs || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>

                        {/* TX Information */}
                        <Tooltip title={
                          connectionDetails?.source === 'uplink'
                            ? `Child STA-mode radio TX histograms (child → parent). Estimated PHY rate is calculated from dominant MCS/NSS/BW${connectionDetails.txRate?.MCS ? ` (MCS ${connectionDetails.txRate.MCS}` : ''}${connectionDetails.txRate?.NSS ? `, NSS ${connectionDetails.txRate.NSS}` : ''}${connectionDetails.txRate?.BW ? `, BW ${connectionDetails.txRate.BW} MHz` : ''}${connectionDetails.txRate?.MCS ? ')' : ''}.${connectionDetails.txTotalBytes ? ` ${(connectionDetails.txTotalBytes / 1e6).toFixed(1)} MB sampled.` : ''} Same source as optimizer.`
                            : 'As measured by Parent AP (parent-side association — MCS/NSS/BW are aliased across all associated stations)'
                        }>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: 'background.default',
                            p: 1,
                            borderRadius: 1,
                            gap: 1
                          }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                              {childLinkCardTitle('ap', connectionDetails?.source)}
                            </Typography>
                            <Box sx={{ 
                              bgcolor: 'background.paper',
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              overflow: 'hidden'
                            }}>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>RSSI</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>Est. Rate</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>NSS</Typography>
                                <Typography variant="caption" sx={{ 
                                  color: 'text.secondary', 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                  fontSize: '0.75rem'
                                }}>MCS</Typography>
                              </Box>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                              }}>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.rssi ? `${connectionDetails.rssi}dBm` : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {(() => {
                                    const rate = connectionDetails.txRate?.Bitrate || connectionDetails.txRate?.bitrate;
                                    return rate ? `${(rate / 1000000).toFixed(2)} Mbps` : 'N/A';
                                  })()}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  borderRight: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.txRate?.NSS || connectionDetails.txRate?.nss || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  textAlign: 'center',
                                  p: 0.5,
                                  fontSize: '0.75rem'
                                }}>
                                  {connectionDetails.txRate?.MCS || connectionDetails.txRate?.mcs || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>
                      </Box>
                    );
                  })()}
                  
                  {/* Association Details for debugging */}
                  {device.wlan_mac && (
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold', mb: 1, display: 'block' }}>
                        Association Details
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        <strong>WLAN MAC:</strong> {device.wlan_mac}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}

      {/* Radio Interfaces Section (omit unused VLAN ifaces with empty mode) */}
      {detailTab === 'radios' && (radioSectionPrivateIfaces.length > 0 || radioSectionPublicIfaces.length > 0) && (
        <Paper elevation={3} sx={{ padding: 3, mb: 3 }}>
          <Tooltip title="Information taken from device report and might take some time to refresh" placement="top">
            <Typography variant="h6" gutterBottom sx={{ cursor: 'help', display: 'inline-block' }}>
              Radio Interfaces
            </Typography>
          </Tooltip>
          <Grid container spacing={2}>
            {/* Private WiFi Interfaces */}
            {radioSectionPrivateIfaces.length > 0 && (
              <Grid item xs={12} sm={6}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 1.5,
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                    height: '100%'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ flexGrow: 1 }}>
                      Private WiFi
                    </Typography>
                    <Chip
                      label={`${radioSectionPrivateIfaces.length} Interface${radioSectionPrivateIfaces.length > 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  </Box>
                  <Stack spacing={1}>
                    {radioSectionPrivateIfaces.map((iface, index) => {
                      // For EdgeCore, Radio.Name might be empty - fallback to first radio
                      const isEdgeCore = device.compatible?.toLowerCase()?.includes('edgecore');
                      const radioName = iface.Radio?.Name || iface.radio?.name || iface.radio?.Ref;
                      let radioInfo = device.radios?.find(r => r.name === radioName);
                      // EdgeCore fallback: if no match, use first radio
                      if (!radioInfo && isEdgeCore && device.radios?.length > 0) {
                        radioInfo = device.radios[0];
                      }
                      // jnet2 fallback: try to match by interface name index (ath1 -> wifi1, ath2 -> wifi2)
                      if (!radioInfo && !isEdgeCore && device.radios?.length > 0) {
                        const ifaceIdx = parseInt((iface.Name || iface.name || '').replace(/\D/g, '')) || (index + 1);
                        radioInfo = device.radios.find(r => {
                          const rIdx = parseInt((r.name || '').replace(/\D/g, ''));
                          return rIdx === ifaceIdx;
                        }) || device.radios[Math.min(index, device.radios.length - 1)];
                      }
                      const isStation = (iface.Mode || iface.mode)?.toLowerCase() === 'station';
                      // Handle both cases for interface properties (EdgeCore uses lowercase)
                      const ifaceName = iface.Name || iface.name;
                      const ifaceSSID = iface.SSID || iface.ssid;
                      const ifaceAssociations = iface.Associations || iface.associations || [];
                      
                      return (
                        <Box 
                          key={`private-${ifaceName || 'iface'}-${index}`}
                          sx={{ 
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.paper'
                          }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                                {isEdgeCore ? (ifaceName || `Interface ${index + 1}`) : `Interface ${index + 1}`}
                              </Typography>
                              <Chip
                                label={isStation ? 'Station' : 'AP'}
                                size="small"
                                color={isStation ? 'warning' : 'primary'}
                                sx={{ height: 20, fontSize: '0.75rem' }}
                              />
                              {!isStation && (
                                <Tooltip title="Number of Associated stations">
                                  <Chip
                                    label={ifaceAssociations.length || 0}
                                    size="small"
                                    color="primary"
                                    sx={{ 
                                      height: 20, 
                                      fontSize: '0.75rem',
                                      ml: 1,
                                      minWidth: '24px'
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            {/* SSID - show for EdgeCore always, for jnet2 only when available */}
                            {(isEdgeCore || ifaceSSID) && (
                            <Tooltip title="SSID" placement="top">
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  color: 'primary.main',
                                  fontWeight: 500,
                                  bgcolor: 'primary.lighter',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  cursor: 'help'
                                }}
                              >
                                  {ifaceSSID || 'N/A'}
                              </Typography>
                            </Tooltip>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              bgcolor: 'background.default',
                              p: 1,
                              borderRadius: 1,
                              gap: 1
                            }}>
                              {/* EdgeCore-specific display */}
                              {isEdgeCore ? (
                              <Box sx={{ 
                                display: 'grid',
                                  gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 1
                              }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Channel</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="body2">{radioInfo?.channel || 'N/A'}</Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Ch. Width</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="body2">{radioInfo?.channel_width ? `${radioInfo.channel_width} MHz` : 'N/A'}</Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>TX Power</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="body2">{radioInfo?.tx_power ? `${radioInfo.tx_power} dBm` : 'N/A'}</Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              ) : (
                                /* jnet2 display - original format */
                                  <Box sx={{ 
                                  display: 'grid',
                                  gridTemplateColumns: isStation ? 'repeat(7, 1fr)' : 'repeat(5, 1fr)',
                                  gap: 1
                                }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Name</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="body2">{ifaceName || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Band</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2">{radioInfo?.band || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Channel</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2">{radioInfo?.channel || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Ch. Width</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2">{radioInfo?.channel_width ? `${radioInfo.channel_width}MHz` : 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>TX Power</Typography>
                                    <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="body2">{iface.TxPower || iface.tx_power ? `${iface.TxPower || iface.tx_power}dBm` : 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                {isStation && (
                                  <>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>ST RSSI</Typography>
                                        <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                              color: iface.RSSI && parseInt(iface.RSSI) < -80 ? 'error.main' : 'inherit',
                                              fontWeight: iface.RSSI && parseInt(iface.RSSI) < -80 ? 500 : 'normal'
                                          }}
                                        >
                                          <Tooltip title="RSSI measured by station radio (RX)">
                                              <span>{iface.RSSI || iface.rssi ? `${iface.RSSI || iface.rssi}dBm` : 'N/A'}</span>
                                          </Tooltip>
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>AP RSSI</Typography>
                                        <Box sx={{ bgcolor: 'background.paper', p: 0.75, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography 
                                          variant="body2"
                                          sx={{ 
                                            color: (() => {
                                                if (!device.parent_serial) return 'inherit';
                                                const parentDevice = meshParent;
                                                if (!parentDevice) return 'inherit';
                                                const deviceMacs = device.radios?.map(radio => radio.mac?.toLowerCase()).filter(Boolean) || [];
                                                if (!deviceMacs.length) return 'inherit';
                                                const findTxRssi = (interfaces) => {
                                                  if (!interfaces) return null;
                                                  for (const parentIface of interfaces) {
                                                    const assocs = parentIface.Associations || parentIface.associations || [];
                                                    for (const assoc of assocs) {
                                                      const stationMac = (assoc.Station || assoc.station || '').toLowerCase();
                                                      if (deviceMacs.includes(stationMac)) {
                                                        return parseInt(assoc.RSSI || assoc.rssi);
                                                      }
                                                    }
                                                  }
                                                  return null;
                                                };
                                                const apRssi = findTxRssi(parentDevice.private_wifi?.interfaces || parentDevice.private_wifi?.Interfaces) || 
                                                              findTxRssi(parentDevice.public_wifi?.interfaces || parentDevice.public_wifi?.Interfaces);
                                              return apRssi && apRssi < -80 ? 'error.main' : 'inherit';
                                            })(),
                                            fontWeight: (() => {
                                                if (!device.parent_serial) return 'normal';
                                                const parentDevice = meshParent;
                                                if (!parentDevice) return 'normal';
                                                const deviceMacs = device.radios?.map(radio => radio.mac?.toLowerCase()).filter(Boolean) || [];
                                                if (!deviceMacs.length) return 'normal';
                                                const findTxRssi = (interfaces) => {
                                                  if (!interfaces) return null;
                                                  for (const parentIface of interfaces) {
                                                    const assocs = parentIface.Associations || parentIface.associations || [];
                                                    for (const assoc of assocs) {
                                                      const stationMac = (assoc.Station || assoc.station || '').toLowerCase();
                                                      if (deviceMacs.includes(stationMac)) {
                                                        return parseInt(assoc.RSSI || assoc.rssi);
                                                      }
                                                    }
                                                  }
                                                  return null;
                                                };
                                                const apRssi = findTxRssi(parentDevice.private_wifi?.interfaces || parentDevice.private_wifi?.Interfaces) || 
                                                              findTxRssi(parentDevice.public_wifi?.interfaces || parentDevice.public_wifi?.Interfaces);
                                              return apRssi && apRssi < -80 ? 500 : 'normal';
                                            })()
                                          }}
                                        >
                                          <Tooltip title="RSSI as seen by parent AP (TX)">
                                            <span>
                                              {(() => {
                                                if (!device.parent_serial) return 'N/A';
                                                const parentDevice = meshParent;
                                                if (!parentDevice) return 'N/A';
                                                  const deviceMacs = device.radios?.map(radio => radio.mac?.toLowerCase()).filter(Boolean) || [];
                                                  if (!deviceMacs.length) return 'N/A';
                                                const findTxRssi = (interfaces) => {
                                                  if (!interfaces) return null;
                                                  for (const parentIface of interfaces) {
                                                      const assocs = parentIface.Associations || parentIface.associations || [];
                                                      for (const assoc of assocs) {
                                                        const stationMac = (assoc.Station || assoc.station || '').toLowerCase();
                                                      if (deviceMacs.includes(stationMac)) {
                                                          return assoc.RSSI || assoc.rssi;
                                                      }
                                                    }
                                                  }
                                                  return null;
                                                };
                                                  const apRssi = findTxRssi(parentDevice.private_wifi?.interfaces || parentDevice.private_wifi?.Interfaces) || 
                                                                findTxRssi(parentDevice.public_wifi?.interfaces || parentDevice.public_wifi?.Interfaces);
                                                return apRssi ? `${apRssi}dBm` : 'N/A';
                                              })()}
                                            </span>
                                          </Tooltip>
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </>
                                )}
                              </Box>
                              )}
                            </Box>
                            {!isStation && deviceSupportsApFailsafe(device) && (
                              <RadioApSegments
                                device={device}
                                treeId={treeIdForApRadio(
                                  device,
                                  radioInfo?.name || radioName || ifaceName,
                                  radioSectionPrivateIfaces
                                    .slice(0, index + 1)
                                    .filter((item) => (item.Mode || item.mode)?.toLowerCase() !== 'station')
                                    .length - 1,
                                )}
                                onChanged={(res) => {
                                  if (!res) return;
                                  setDevice((prev) => {
                                    if (!prev) return prev;
                                    const next = { ...prev };
                                    if (res.segment) next.segment = res.segment;
                                    if (Array.isArray(res.ap_segments)) next.ap_segments = res.ap_segments;
                                    return next;
                                  });
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              </Grid>
            )}

            {/* Public WiFi Interfaces */}
            {radioSectionPublicIfaces.length > 0 && (
              <Grid item xs={12} sm={6}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 1.5,
                    borderLeft: '4px solid',
                    borderColor: 'info.main',
                    height: '100%'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="info.main" sx={{ flexGrow: 1 }}>
                      Public WiFi
                    </Typography>
                    <Chip
                      label={`${radioSectionPublicIfaces.length} Interface${radioSectionPublicIfaces.length > 1 ? 's' : ''}`}
                      size="small"
                      color="info"
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  </Box>
                  <Stack spacing={1}>
                    {radioSectionPublicIfaces.map((iface, index) => {
                      // Handle both cases for interface properties (jnet2 uses lowercase)
                      const radioName = iface.Radio?.Name || iface.radio?.name || iface.radio?.Name;
                      const radioInfo = device.radios?.find(r => r.name === radioName);
                      const isStation = (iface.Mode || iface.mode)?.toLowerCase() === 'station';
                      const ifaceName = iface.Name || iface.name;
                      const ifaceSSID = iface.SSID || iface.ssid;
                      const assocCount = !isStation
                        ? publicWifiClientCountForInterface(iface, device?.public_wifi, radioInfo?.band)
                        : 0;
                      
                      return (
                        <Box 
                          key={`public-${ifaceName || 'iface'}-${index}`}
                          sx={{ 
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.paper'
                          }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                                Interface {index + 1}
                              </Typography>
                              <Chip
                                label={isStation ? 'Station' : 'AP'}
                                size="small"
                                color={isStation ? 'warning' : 'info'}
                                sx={{ height: 20, fontSize: '0.75rem' }}
                              />
                              {!isStation && (
                                <Tooltip title="Number of associated stations">
                                  <Chip
                                    label={assocCount}
                                    size="small"
                                    color="info"
                                    sx={{ 
                                      height: 20, 
                                      fontSize: '0.75rem',
                                      ml: 1,
                                      minWidth: '24px'
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                            <Tooltip title="SSID" placement="top">
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  color: 'info.main',
                                  fontWeight: 500,
                                  bgcolor: 'info.lighter',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  cursor: 'help'
                                }}
                              >
                                {ifaceSSID || 'N/A'}
                              </Typography>
                            </Tooltip>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              bgcolor: 'background.default',
                              p: 1,
                              borderRadius: 1,
                              gap: 1
                            }}>
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: isStation ? 'repeat(6, 1fr)' : 'repeat(5, 1fr)',
                                gap: 1
                              }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Name</Typography>
                                  <Box sx={{ 
                                    bgcolor: 'background.paper',
                                    p: 0.75,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}>
                                    <Typography variant="body2">{ifaceName || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Band</Typography>
                                  <Box sx={{ 
                                    bgcolor: 'background.paper',
                                    p: 0.75,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}>
                                    <Typography variant="body2">{radioInfo?.band || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Channel</Typography>
                                  <Box sx={{ 
                                    bgcolor: 'background.paper',
                                    p: 0.75,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}>
                                    <Typography variant="body2">{radioInfo?.channel || 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Ch. Width</Typography>
                                  <Box sx={{ 
                                    bgcolor: 'background.paper',
                                    p: 0.75,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}>
                                    <Typography variant="body2">{radioInfo?.channel_width ? `${radioInfo.channel_width}MHz` : 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>TX Power</Typography>
                                  <Box sx={{ 
                                    bgcolor: 'background.paper',
                                    p: 0.75,
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}>
                                    <Typography variant="body2">{(iface.TxPower || iface.tx_power) ? `${iface.TxPower || iface.tx_power}dBm` : 'N/A'}</Typography>
                                  </Box>
                                </Box>
                                {isStation && (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>RSSI</Typography>
                                    <Box sx={{ 
                                      bgcolor: 'background.paper',
                                      p: 0.75,
                                      borderRadius: 0.5,
                                      border: '1px solid',
                                      borderColor: 'divider'
                                    }}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          color: (() => {
                                            const rssi = parseInt(iface.RSSI || iface.rssi);
                                            return rssi && rssi < -80 ? 'error.main' : 'inherit';
                                          })(),
                                          fontWeight: (() => {
                                            const rssi = parseInt(iface.RSSI || iface.rssi);
                                            return rssi && rssi < -80 ? 500 : 'normal';
                                          })()
                                        }}
                                      >
                                        <Tooltip title="RSSI measure by station radio">
                                          <span>{(iface.RSSI || iface.rssi) ? `${iface.RSSI || iface.rssi}dBm` : 'N/A'}</span>
                                        </Tooltip>
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}
        </Box>
        </Box>

      {/* Configuration Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={closeConfigDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6">Device Configuration</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={configMode}
                onChange={(_, next) => { if (next) handleSwitchConfigMode(next); }}
              >
                <ToggleButton value="fields">Tree-Mgmt Fields</ToggleButton>
                <ToggleButton value="public-wifi">Public WiFi</ToggleButton>
                <ToggleButton value="raw">Raw JSON</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {configMode === 'raw' && (
              <Tooltip title="Format JSON">
                <IconButton 
                  onClick={handleFormatJson} 
                  color="primary" 
                  size="small"
                  disabled={configLoading}
                  sx={{ 
                    width: 32, 
                    height: 32,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <FormatIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              )}
              {configMode === 'raw' && (
              <Tooltip title="Reset Changes">
                <IconButton 
                  onClick={handleReset} 
                  color="secondary" 
                  size="small"
                  disabled={configLoading}
                  sx={{ 
                    width: 32, 
                    height: 32,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <ResetIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              )}
              {configMode !== 'public-wifi' && (
              <Tooltip title="Refresh">
                <IconButton 
                  onClick={configMode === 'fields' ? fetchTreeMgmtView : fetchDeviceConfig}
                  color="primary"
                  size="small"
                  disabled={configMode === 'fields' ? treeMgmtLoading : configLoading}
                  sx={{ 
                    width: 32, 
                    height: 32,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              )}
              {device && (device.capabilities?.model === 'JuganuNode' || device.capabilities?.model === 'JuganuCAP') && (
                <Tooltip
                  title={
                    <Box sx={{ p: 1, maxWidth: 400 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, fontSize: '0.9rem' }}>
                        📋 Configuration Help for {device.capabilities?.model} - 6GHz to 5GHz Switch
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
                            📡 Radio Configuration
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Set <code style={{ backgroundColor: '#e0e0e0', padding: '1px 3px', borderRadius: '2px', fontSize: '0.75rem' }}>"band": 2</code>
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                            (1=2.4GHz, 2=5GHz, 3=6GHz)
                          </Typography>
                        </Box>

                        <Box sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
                            🌳 TreeMgmt Configuration
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.3 }}>
                            1. Set <code style={{ backgroundColor: '#e0e0e0', padding: '1px 3px', borderRadius: '2px', fontSize: '0.75rem' }}>"band": "5GHz"</code>
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.3 }}>
                            2. Replace <code style={{ backgroundColor: '#e0e0e0', padding: '1px 3px', borderRadius: '2px', fontSize: '0.75rem' }}>"ChannelGroups"</code> with <code style={{ backgroundColor: '#e0e0e0', padding: '1px 3px', borderRadius: '2px', fontSize: '0.75rem' }}>"ChannelGroupIDs"</code>
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                            Use [1, 2] for 5GHz, [3, 4, 5, 6] for 6GHz
                          </Typography>
                        </Box>

                        <Box sx={{ p: 1, bgcolor: 'grey.900', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem', color: 'grey.300' }}>
                            Example 5GHz Configuration:
                          </Typography>
                          <code style={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.3, whiteSpace: 'pre-wrap', color: 'white' }}>
                            {`"radio": [{"band": "2", "radio": "5GHz_0"}],\n"treemgmt": [{"band": "5GHz", "ChannelGroupIDs": [1, 2]}]`}
                          </code>
                        </Box>
                      </Box>
                    </Box>
                  }
                  placement="bottom-start"
                  arrow
                  PopperProps={{
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        boxShadow: 3,
                        fontSize: '0.75rem',
                        maxWidth: 'none',
                        p: 0,
                      },
                      '& .MuiTooltip-arrow': {
                        color: 'background.paper',
                      }
                    }
                  }}
                >
                  <IconButton 
                    size="small" 
                    sx={{ 
                      color: 'info.main',
                      width: 32, 
                      height: 32,
                      borderRadius: 1,
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <HelpIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {configMode === 'raw' && configError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {configError}
            </Alert>
          )}
          {configMode === 'fields' && treeMgmtError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {treeMgmtError}
            </Alert>
          )}
          {saveStatus.message && (
            <Alert severity={saveStatus.type || 'info'} sx={{ mb: 2 }}>
              {saveStatus.message}
            </Alert>
          )}
          {applyingConfig && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
            </Box>
          )}

          {configMode === 'public-wifi' ? (
            <DevicePublicWifiConfigPanel
              device={device}
              serial={serial}
              onApplied={async () => {
                await fetchDeviceData();
              }}
            />
          ) : configMode === 'fields' ? (
              <Box>
                <Tabs
                  value={fieldsSubTab}
                  onChange={(_, v) => setFieldsSubTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  <Tab value="general" label="General" />
                  <Tab value="wifi" label="WiFi" />
                  <Tab value="network" label="Network" />
                  <Tab value="port-forwarding" label="Port Forwarding" />
                  <Tab value="firewall" label="Firewall" />
                </Tabs>

                {fieldsSubTab === 'general' && (
                  <Box>
                    <ReportsSection
                      serial={serial}
                      isJnet2={device?.capabilities?.compatible === 'jnet2'}
                    />
                    <SyslogSection
                      serial={serial}
                      isJnet2={device?.capabilities?.compatible === 'jnet2'}
                    />
                  </Box>
                )}

                {fieldsSubTab === 'network' && (
                  treeMgmtLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : !treeMgmtView ? (
                    <Alert severity="info">No tree-management configuration available.</Alert>
                  ) : (
                <Paper variant="outlined" sx={fieldSectionSx}>
                  <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                    Network
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Topology controls whether this device runs a DHCP server. Static IP is optional for CAP and node devices.
                    Changing topology requires a reboot and may cause a service interruption.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                    {(() => {
                      const role = treeMgmtView.device_type === 'cap' ? 'cap' : 'node';
                      const allowedTopologies = TOPOLOGY_OPTIONS.filter((opt) => opt.roles.includes(role));
                      const currentTopology = topologyValue();
                      const selectTopology = allowedTopologies.some((o) => o.value === currentTopology)
                        ? currentTopology
                        : (allowedTopologies[0]?.value || '1');
                      const selectedOpt = TOPOLOGY_OPTIONS.find((o) => o.value === selectTopology);
                      return (
                    <FormControl size="small" sx={{ minWidth: 280 }}>
                      <InputLabel>Topology</InputLabel>
                      <Select
                        label="Topology"
                        value={selectTopology}
                        onChange={(e) => setTopologyEdit(e.target.value)}
                      >
                        {allowedTopologies.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <Box>
                                <Typography variant="body2">{opt.label}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {opt.helper}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                      </Select>
                      <FormHelperText>
                        {selectedOpt?.helper || ''}
                      </FormHelperText>
                    </FormControl>
                      );
                    })()}
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isStaticIpEnabled()}
                        onChange={(e) => setUseStaticIpEdit(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Use static IP"
                  />
                  {isStaticIpEnabled() && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
                      <TextField
                        size="small"
                        label="IP address"
                        sx={{ width: 160 }}
                        value={staticIpFieldValue('ipaddr')}
                        onChange={(e) => setStaticIpFieldEdit('ipaddr', e.target.value)}
                      />
                      <TextField
                        size="small"
                        label="Netmask"
                        sx={{ width: 160 }}
                        value={staticIpFieldValue('netmask')}
                        onChange={(e) => setStaticIpFieldEdit('netmask', e.target.value)}
                      />
                      <TextField
                        size="small"
                        label="Gateway"
                        sx={{ width: 160 }}
                        value={staticIpFieldValue('gateway')}
                        onChange={(e) => setStaticIpFieldEdit('gateway', e.target.value)}
                      />
                      <TextField
                        size="small"
                        label="DNS"
                        sx={{ width: 160 }}
                        value={staticIpFieldValue('dns')}
                        onChange={(e) => setStaticIpFieldEdit('dns', e.target.value)}
                      />
                    </Box>
                  )}
                </Paper>
                  )
                )}

                {fieldsSubTab === 'wifi' && (
                  treeMgmtLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : !treeMgmtView ? (
                    <Alert severity="info">No tree-management configuration available.</Alert>
                  ) : (
                    <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Field updates only modify the tree-management portion of the device
                  config. Public WiFi settings (SSIDs / radio profiles) are preserved —
                  use the Public WiFi tab to assign those profiles.
                </Alert>
                <Tabs
                  value={wifiTab}
                  onChange={(_, v) => setWifiInnerTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 2,
                    minHeight: 36,
                    '& .MuiTab-root': { minHeight: 36, textTransform: 'none' },
                  }}
                >
                  {showClientRadioTab && <Tab value="client" label="Client Radio" />}
                  <Tab value="ap" label="AP Radio" />
                  <Tab value="segments" label="Segments" />
                </Tabs>
                {wifiTab === 'ap' && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Tooltip
                    title={
                      canAddTree()
                        ? 'Add a new treemgmt entry'
                        : `${treeMgmtView.device_type === 'cap' ? 'CAP' : 'Non-CAP'} devices may have at most ${maxAllowedTrees()} treemgmt entr${maxAllowedTrees() === 1 ? 'y' : 'ies'}`
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={openAddTreeDialog}
                        disabled={!canAddTree() || availableTreeIDs().length === 0}
                      >
                        Add Tree
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                )}

                {/* Client Radio section — only for non-CAP (Node) devices.
                    tx_power_up controls the STA radio that connects upward
                    to the parent; not meaningful on CAP (root) devices. */}
                {wifiTab === 'client' && showClientRadioTab && (() => {
                  const radioTree = clientRadioTree();
                  if (!radioTree) return null;
                  return (
                    <Paper variant="outlined" sx={fieldSectionSx}>
                      <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                        Client Radio
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Transmit power and closed-loop RSSI targets for the radio that connects this device upward to its parent.
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <TextField
                          size="small"
                          label="TX Power Up (dBm)"
                          type="number"
                          sx={{ width: 160 }}
                          value={treeFieldValue(radioTree, 'tx_power_up')}
                          onChange={(e) => setTreeEdit(radioTree.tree_id, 'tx_power_up', e.target.value)}
                        />
                        {treeMgmtView.supports_station_closed_loop && (
                          <>
                            <TextField
                              size="small"
                              label="Target Min AP RSSI (dBm)"
                              type="text"
                              inputMode="numeric"
                              sx={{ width: 200 }}
                              value={stationLoopFieldValue('station_target_min_ap_rssi')}
                              onChange={(e) => setStationLoopEdit('station_target_min_ap_rssi', e.target.value)}
                              onKeyDown={blockStationLoopRssiInputKeys}
                              InputProps={stationLoopRssiInputProps()}
                              inputProps={{ 'aria-label': 'Target Min AP RSSI dBm' }}
                              helperText={!treeMgmtView.has_station_closed_loop ? 'Applied when closed-loop entry is created' : undefined}
                            />
                            <TextField
                              size="small"
                              label="Target Max AP RSSI (dBm)"
                              type="text"
                              inputMode="numeric"
                              sx={{ width: 200 }}
                              value={stationLoopFieldValue('station_target_max_ap_rssi')}
                              onChange={(e) => setStationLoopEdit('station_target_max_ap_rssi', e.target.value)}
                              onKeyDown={blockStationLoopRssiInputKeys}
                              InputProps={stationLoopRssiInputProps()}
                              inputProps={{ 'aria-label': 'Target Max AP RSSI dBm' }}
                              helperText={!treeMgmtView.has_station_closed_loop ? 'Applied when closed-loop entry is created' : undefined}
                            />
                          </>
                        )}
                      </Box>
                    </Paper>
                  );
                })()}

                {wifiTab === 'segments' && (treeMgmtView.trees || []).length > 0 && (
                  <Paper variant="outlined" sx={fieldSectionSx}>
                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                      Segments
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Station segments apply to this device. AP segments are set per backhaul radio.
                      FEFE accepts clients from any segment.
                    </Typography>
                    {treeMgmtView.device_type !== 'cap' && (
                      <Box sx={{ mb: 2 }}>
                        <SegmentIdListEditor
                          label="Station (segment_id_dev)"
                          helper="Written to every treemgmt row on this node"
                          ids={
                            treeMgmtEdits[STATION_SEGMENT_EDIT_KEY]?.segment_id_dev
                            ?? treeMgmtView.segment_id_dev
                            ?? []
                          }
                          onChange={(ids) => {
                            setTreeMgmtEdits((prev) => {
                              const next = { ...prev };
                              if (sameSegmentIdList(ids, treeMgmtView.segment_id_dev || [])) {
                                delete next[STATION_SEGMENT_EDIT_KEY];
                              } else {
                                next[STATION_SEGMENT_EDIT_KEY] = { segment_id_dev: ids };
                              }
                              return next;
                            });
                          }}
                        />
                      </Box>
                    )}
                    {(treeMgmtView.trees || []).map((tree) => {
                      const maxChildren = parseInt(treeFieldValue(tree, 'max_children'), 10);
                      const apBlocked = maxChildren === 0;
                      const edited = treeMgmtEdits[tree.tree_id];
                      const apIds = edited?.segment_id_ap !== undefined
                        ? edited.segment_id_ap
                        : (tree.segment_id_ap || []);
                      return (
                        <Box key={`seg-${tree.tree_id}`} sx={{ mb: 2 }}>
                          <SegmentIdListEditor
                            label={`AP ${tree.tree_id} (segment_id_ap)`}
                            helper={apBlocked ? 'Unavailable while max children is 0' : 'Children accepted on this radio'}
                            ids={apIds}
                            disabled={apBlocked}
                            onChange={(ids) => {
                              if (sameSegmentIdList(ids, tree.segment_id_ap || [])) {
                                setTreeEdit(tree.tree_id, 'segment_id_ap', undefined);
                              } else {
                                setTreeEdit(tree.tree_id, 'segment_id_ap', ids);
                              }
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Paper>
                )}

                {wifiTab === 'ap' && (treeMgmtView.trees || []).length === 0 && (
                  <Alert severity="warning">This device has no treemgmt entries.</Alert>
                )}
                {wifiTab === 'segments' && (treeMgmtView.trees || []).length === 0 && (
                  <Alert severity="warning">This device has no treemgmt entries.</Alert>
                )}
                {wifiTab === 'ap' && (treeMgmtView.trees || []).map((tree) => {
                  const mode = channelModeForTree(tree);
                  return (
                  <Paper key={tree.tree_id} variant="outlined" sx={fieldSectionSx}>
                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                      AP Radio
                      {(treeMgmtView.trees || []).length > 1 && (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontFamily: 'monospace' }}>
                          {tree.tree_id}
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Band</InputLabel>
                        <Select
                          label="Band"
                          value={treeFieldValue(tree, 'band')}
                          onChange={(e) => {
                            const newBand = e.target.value;
                            setTreeEdit(tree.tree_id, 'band', newBand);
                            // Drop any pending UNII ids that don't belong to the
                            // newly chosen band so the user doesn't accidentally
                            // submit a cross-band selection.
                            const currentIds = parseChannelGroupIds(channelGroupIdsValue(tree));
                            const filtered = currentIds.filter(id => UNII_BY_ID[id]?.band === newBand);
                            if (filtered.length !== currentIds.length) {
                              setTreeEdit(tree.tree_id, 'channel_group_ids', filtered.join(', '));
                            }
                          }}
                        >
                          <MenuItem value="5GHz">5GHz</MenuItem>
                          <MenuItem value="6GHz">6GHz</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>HT Mode</InputLabel>
                        <Select
                          label="HT Mode"
                          value={treeFieldValue(tree, 'htmode')}
                          onChange={(e) => setTreeEdit(tree.tree_id, 'htmode', e.target.value)}
                        >
                          <MenuItem value="HT20">HT20</MenuItem>
                          <MenuItem value="HT40">HT40</MenuItem>
                          <MenuItem value="HT80">HT80</MenuItem>
                          <MenuItem value="HT160">HT160</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="TX Power Down (dBm)"
                        type="number"
                        sx={{ width: 170 }}
                        value={treeFieldValue(tree, 'tx_power_down')}
                        onChange={(e) => setTreeEdit(tree.tree_id, 'tx_power_down', e.target.value)}
                      />
                      <TextField
                        size="small"
                        label="Max Children"
                        type="number"
                        sx={{ width: 140 }}
                        value={treeFieldValue(tree, 'max_children')}
                        onChange={(e) => {
                          setTreeEdit(tree.tree_id, 'max_children', e.target.value);
                          if (parseInt(e.target.value, 10) === 0) {
                            setTreeEdit(tree.tree_id, 'segment_id_ap', undefined);
                          }
                        }}
                      />
                    </Box>

                    <Divider sx={{ my: 1.5 }}>
                      <Chip label="Channel selection" size="small" />
                    </Divider>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                      <Box>
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={mode}
                          onChange={(_, v) => v && setTreeEdit(tree.tree_id, 'channel_mode', v)}
                        >
                          <ToggleButton value="single">Single Channel</ToggleButton>
                          <ToggleButton value="group_ids">Channel Group IDs</ToggleButton>
                          <ToggleButton value="groups">Raw Channel Groups</ToggleButton>
                        </ToggleButtonGroup>
                        <FormHelperText sx={{ mt: 0.5 }}>
                          {mode === 'single' && 'Pin the radio to a specific channel (0 = device picks automatically).'}
                          {mode === 'group_ids' && 'Pick one or more U-NII sub-bands. Device auto-picks the best channel; fixed channel is reset to 0.'}
                          {mode === 'groups' && 'Provide an explicit list of channels per band. Device auto-picks the best one; fixed channel is reset to 0.'}
                        </FormHelperText>
                      </Box>
                      {mode === 'single' && (
                        <TextField
                          size="small"
                          label="Channel"
                          type="number"
                          sx={{ width: 140 }}
                          value={treeFieldValue(tree, 'channel')}
                          onChange={(e) => setTreeEdit(tree.tree_id, 'channel', e.target.value)}
                        />
                      )}
                      {mode === 'group_ids' && (() => {
                        const currentBand = treeFieldValue(tree, 'band');
                        const allIds = parseChannelGroupIds(channelGroupIdsValue(tree));
                        // Hide cross-band IDs: if the band picker still shows
                        // an id from the other band (e.g. legacy data), surface
                        // a warning rather than silently dropping it.
                        const inBandIds = allIds.filter(id => UNII_BY_ID[id]?.band === currentBand);
                        const offBandIds = allIds.filter(id => UNII_BY_ID[id]?.band && UNII_BY_ID[id].band !== currentBand);
                        const allowedOptions = UNII_GROUPS.filter(g => g.band === currentBand);
                        const selectedOptions = allowedOptions.filter(g => inBandIds.includes(g.id));
                        const summary = summarizeUniiSelection(inBandIds);
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 360, flexGrow: 1 }}>
                            <Autocomplete
                              multiple
                              size="small"
                              disableCloseOnSelect
                              options={allowedOptions}
                              value={selectedOptions}
                              isOptionEqualToValue={(opt, val) => opt.id === val.id}
                              getOptionLabel={(opt) => formatUniiOption(opt)}
                              onChange={(_, v) => setTreeEdit(
                                tree.tree_id,
                                'channel_group_ids',
                                v.map(o => o.id).sort((a, b) => a - b).join(', '),
                              )}
                              renderTags={(value, getTagProps) =>
                                value.map((opt, index) => (
                                  <Chip
                                    {...getTagProps({ index })}
                                    key={opt.id}
                                    size="small"
                                    label={`${opt.id} · ${opt.label}`}
                                    sx={{ height: 22 }}
                                  />
                                ))
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={`Channel Groups (${currentBand} U-NII sub-bands)`}
                                  placeholder={selectedOptions.length === 0 ? `Pick one or more ${currentBand} U-NII sub-bands...` : ''}
                                />
                              )}
                            />
                            {summary && (
                              <Alert severity="info" variant="outlined" sx={{ py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                                {summary}
                              </Alert>
                            )}
                            {offBandIds.length > 0 && (
                              <Alert
                                severity="warning"
                                variant="outlined"
                                sx={{ py: 0, '& .MuiAlert-message': { py: 0.5 } }}
                                action={
                                  <Button
                                    size="small"
                                    onClick={() => setTreeEdit(
                                      tree.tree_id,
                                      'channel_group_ids',
                                      inBandIds.join(', '),
                                    )}
                                  >
                                    Drop them
                                  </Button>
                                }
                              >
                                {`Currently saved off-band IDs ${offBandIds.join(', ')} don't match band ${currentBand} and will be ignored.`}
                              </Alert>
                            )}
                          </Box>
                        );
                      })()}
                      {mode === 'groups' && (() => {
                        const currentBand = treeFieldValue(tree, 'band');
                        const is5G = currentBand === '5GHz';
                        const is6G = currentBand === '6GHz';
                        return (
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField
                              size="small"
                              label="5GHz channels"
                              sx={{ width: 220 }}
                              placeholder="e.g. 36, 40, 44"
                              disabled={!is5G}
                              helperText={!is5G ? 'Not available on this band' : ''}
                              value={channelGroupsValueForBand(tree, '5GHz')}
                              onChange={(e) => setTreeEdit(tree.tree_id, 'channel_groups_5GHz', e.target.value)}
                            />
                            <TextField
                              size="small"
                              label="6GHz channels"
                              sx={{ width: 220 }}
                              placeholder="e.g. 1, 5, 9"
                              disabled={!is6G}
                              helperText={!is6G ? 'Not available on this band' : ''}
                              value={channelGroupsValueForBand(tree, '6GHz')}
                              onChange={(e) => setTreeEdit(tree.tree_id, 'channel_groups_6GHz', e.target.value)}
                            />
                          </Box>
                        );
                      })()}
                    </Box>

                    {treeMgmtView.device_type !== 'cap' && (
                    <Accordion sx={{ mt: 2 }} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">Background STA scan</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id={`bg-scan-on-${tree.tree_id}`} shrink>BG Scan</InputLabel>
                            <Select
                              labelId={`bg-scan-on-${tree.tree_id}`}
                              label="BG Scan"
                              value={bgFieldValue(tree, 'bg_scan_on_off') || ''}
                              displayEmpty
                              renderValue={bgScanSelectRenderValue}
                              onChange={(e) => setTreeEdit(tree.tree_id, 'bg_bg_scan_on_off', e.target.value)}
                            >
                              <MenuItem value=""><em>(unchanged)</em></MenuItem>
                              <MenuItem value="on">on</MenuItem>
                              <MenuItem value="off">off</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            type="number"
                            sx={{ width: 200 }}
                            label={(
                              <FieldLabelTip title="How long to wait between scanning one channel and the other (idle time between two channel scanning).">
                                Channel scan interval (s)
                              </FieldLabelTip>
                            )}
                            InputLabelProps={{ shrink: true }}
                            value={bgFieldValue(tree, 'chan_scan_interval')}
                            onChange={(e) => setTreeEdit(tree.tree_id, 'bg_chan_scan_interval', e.target.value)}
                          />
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id={`bg-fr-${tree.tree_id}`} shrink>Fast recovery</InputLabel>
                            <Select
                              labelId={`bg-fr-${tree.tree_id}`}
                              label="Fast recovery"
                              value={bgFieldValue(tree, 'fr_on_off') || ''}
                              displayEmpty
                              renderValue={bgScanSelectRenderValue}
                              onChange={(e) => setTreeEdit(tree.tree_id, 'bg_fr_on_off', e.target.value)}
                            >
                              <MenuItem value=""><em>(unchanged)</em></MenuItem>
                              <MenuItem value="on">on</MenuItem>
                              <MenuItem value="off">off</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            type="number"
                            sx={{ width: 180 }}
                            label={(
                              <FieldLabelTip title="How often to run the full bg scan">
                                Scan interval (s)
                              </FieldLabelTip>
                            )}
                            InputLabelProps={{ shrink: true }}
                            value={bgFieldValue(tree, 'scan_interval')}
                            onChange={(e) => setTreeEdit(tree.tree_id, 'bg_scan_interval', e.target.value)}
                          />
                          <TextField
                            size="small"
                            label="Self improvement"
                            sx={{ width: 180 }}
                            value={bgSelfImprovementDisplay(tree)}
                            onChange={(e) => setTreeEdit(
                              tree.tree_id,
                              'bg_self_improvment',
                              normalizeBgSelfImprovementInput(e.target.value),
                            )}
                          />
                        </Box>
                        {(() => {
                          const currentBand = treeFieldValue(tree, 'band');
                          const is5G = currentBand === '5GHz';
                          const is6G = currentBand === '6GHz';
                          return (
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                              <TextField
                                size="small"
                                label="ChannelGroups (5GHz)"
                                sx={{ width: 240 }}
                                value={bgChannelGroupsDisplayForBand(tree, '5GHz')}
                                InputProps={{ readOnly: true }}
                                disabled={!is5G}
                                helperText={!is5G ? 'Not available on this band' : 'background_sta_scan scan targets'}
                              />
                              <TextField
                                size="small"
                                label="ChannelGroups (6GHz)"
                                sx={{ width: 240 }}
                                value={bgChannelGroupsDisplayForBand(tree, '6GHz')}
                                InputProps={{ readOnly: true }}
                                disabled={!is6G}
                                helperText={!is6G ? 'Not available on this band' : 'background_sta_scan scan targets'}
                              />
                            </Box>
                          );
                        })()}
                      </AccordionDetails>
                    </Accordion>
                    )}
                  </Paper>
                  );
                })}
                    </Box>
                  )
                )}

                {fieldsSubTab === 'port-forwarding' && (
                  <PortForwardingSection
                    serial={serial}
                    isJnet2={device?.capabilities?.compatible === 'jnet2'}
                  />
                )}

                {fieldsSubTab === 'firewall' && (
                  <FirewallSection
                    serial={serial}
                    isJnet2={device?.capabilities?.compatible === 'jnet2'}
                  />
                )}

              </Box>
          ) : (
            configLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Raw mode overwrites the entire device configuration, including the
                  Public WiFi <code>ap[]</code> section. Prefer Tree-Mgmt Fields for
                  backhaul changes and the Public WiFi tab for SSID / radio profile apply.
                </Alert>
                <Box sx={{ height: '500px', border: '1px solid #ccc', borderRadius: 1, mb: 2 }}>
                  <Editor
                    height="100%"
                    defaultLanguage="json"
                    value={configJson}
                    onChange={handleEditorChange}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 14,
                      wordWrap: 'on',
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                    }}
                  />
                </Box>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfigDialog}>
            {configMode === 'public-wifi' ? 'Close' : 'Cancel'}
          </Button>
          {configMode === 'fields' &&
            (fieldsSubTab === 'wifi' || fieldsSubTab === 'network' || hasPendingTreeMgmtEdits()) && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setTreeMgmtEdits({});
                  setStationClosedLoopEdits({});
                  setNetworkAttrEdits({});
                }}
                startIcon={<ResetIcon />}
                disabled={treeMgmtLoading || applyingConfig || !hasPendingTreeMgmtEdits()}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveTreeMgmt}
                disabled={treeMgmtLoading || applyingConfig || !hasPendingTreeMgmtEdits()}
                startIcon={<SaveIcon />}
              >
                Apply Changes
              </Button>
            </>
          )}
          {configMode === 'raw' && (
            <>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<ResetIcon />}
                disabled={configLoading || applyingConfig}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveConfig}
                disabled={!isJsonValid || configLoading || applyingConfig}
                startIcon={<SaveIcon />}
              >
                Save Configuration
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Tree-Mgmt Entry Dialog */}
      <Dialog open={addTreeOpen} onClose={() => setAddTreeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Tree Management Entry</DialogTitle>
        <DialogContent>
          {addTreeStatus.message && (
            <Alert severity={addTreeStatus.type || 'info'} sx={{ mb: 2 }}>
              {addTreeStatus.message}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            tree_id must match an existing radio name on this device that isn't already
            referenced by another treemgmt entry.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>tree_id</InputLabel>
              <Select
                label="tree_id"
                value={addTreeDraft.tree_id}
                onChange={(e) => setAddTreeDraft(d => ({ ...d, tree_id: e.target.value }))}
              >
                {availableTreeIDs().length === 0 && (
                  <MenuItem value="" disabled>
                    <em>No available radios</em>
                  </MenuItem>
                )}
                {availableTreeIDs().map(rid => (
                  <MenuItem key={rid} value={rid}>{rid}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Band</InputLabel>
              <Select
                label="Band"
                value={addTreeDraft.band}
                onChange={(e) => setAddTreeDraft(d => {
                  const newBand = e.target.value;
                  // Drop any drafted UNII ids and the off-band raw channels so
                  // the user can't accidentally submit a cross-band entry.
                  const offBand = newBand === '5GHz' ? '6GHz' : '5GHz';
                  return {
                    ...d,
                    band: newBand,
                    channel_group_ids: d.channel_group_ids.filter(id => UNII_BY_ID[id]?.band === newBand),
                    [`channel_groups_${offBand}`]: '',
                  };
                })}
              >
                <MenuItem value="5GHz">5GHz</MenuItem>
                <MenuItem value="6GHz">6GHz</MenuItem>
              </Select>
            </FormControl>
            <Divider>
              <Chip label="Channel selection" size="small" />
            </Divider>
            <Box>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={addTreeDraft.channel_mode}
                onChange={(_, v) => v && setAddTreeDraft(d => ({ ...d, channel_mode: v }))}
              >
                <ToggleButton value="single">Single Channel</ToggleButton>
                <ToggleButton value="group_ids">Channel Group IDs</ToggleButton>
                <ToggleButton value="groups">Raw Channel Groups</ToggleButton>
              </ToggleButtonGroup>
              <FormHelperText sx={{ mt: 0.5 }}>
                {addTreeDraft.channel_mode === 'single' && 'Pin the radio to a specific channel (0 = device picks automatically).'}
                {addTreeDraft.channel_mode === 'group_ids' && 'Pick one or more U-NII sub-bands. Device auto-picks the best channel; fixed channel is left at 0.'}
                {addTreeDraft.channel_mode === 'groups' && 'Provide an explicit list of channels per band. Device auto-picks the best one; fixed channel is left at 0.'}
              </FormHelperText>
            </Box>
            {addTreeDraft.channel_mode === 'single' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="Channel"
                  type="number"
                  fullWidth
                  value={addTreeDraft.channel}
                  onChange={(e) => setAddTreeDraft(d => ({ ...d, channel: e.target.value }))}
                />
              </Box>
            )}
            {addTreeDraft.channel_mode === 'group_ids' && (() => {
              const allowedOptions = UNII_GROUPS.filter(g => g.band === addTreeDraft.band);
              const selectedOptions = allowedOptions.filter(g => addTreeDraft.channel_group_ids.includes(g.id));
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Autocomplete
                    multiple
                    size="small"
                    disableCloseOnSelect
                    options={allowedOptions}
                    value={selectedOptions}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    getOptionLabel={(opt) => formatUniiOption(opt)}
                    onChange={(_, v) => setAddTreeDraft(d => ({
                      ...d,
                      channel_group_ids: v.map(o => o.id).sort((a, b) => a - b),
                    }))}
                    renderTags={(value, getTagProps) =>
                      value.map((opt, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={opt.id}
                          size="small"
                          label={`${opt.id} · ${opt.label}`}
                          sx={{ height: 22 }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Channel Groups (${addTreeDraft.band} U-NII sub-bands)`}
                        placeholder={addTreeDraft.channel_group_ids.length === 0 ? `Pick one or more ${addTreeDraft.band} U-NII sub-bands...` : ''}
                      />
                    )}
                  />
                  {summarizeUniiSelection(addTreeDraft.channel_group_ids) && (
                    <Alert severity="info" variant="outlined" sx={{ py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                      {summarizeUniiSelection(addTreeDraft.channel_group_ids)}
                    </Alert>
                  )}
                </Box>
              );
            })()}
            {addTreeDraft.channel_mode === 'groups' && (() => {
              const is5G = addTreeDraft.band === '5GHz';
              const is6G = addTreeDraft.band === '6GHz';
              return (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="5GHz channels"
                    fullWidth
                    placeholder="e.g. 36, 40, 44"
                    disabled={!is5G}
                    helperText={!is5G ? 'Not available on this band' : ''}
                    value={addTreeDraft.channel_groups_5GHz}
                    onChange={(e) => setAddTreeDraft(d => ({ ...d, channel_groups_5GHz: e.target.value }))}
                  />
                  <TextField
                    size="small"
                    label="6GHz channels"
                    fullWidth
                    placeholder="e.g. 1, 5, 9"
                    disabled={!is6G}
                    helperText={!is6G ? 'Not available on this band' : ''}
                    value={addTreeDraft.channel_groups_6GHz}
                    onChange={(e) => setAddTreeDraft(d => ({ ...d, channel_groups_6GHz: e.target.value }))}
                  />
                </Box>
              );
            })()}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>HT Mode</InputLabel>
                <Select
                  label="HT Mode"
                  value={addTreeDraft.htmode}
                  onChange={(e) => setAddTreeDraft(d => ({ ...d, htmode: e.target.value }))}
                >
                  <MenuItem value=""><em>(default)</em></MenuItem>
                  <MenuItem value="HT20">HT20</MenuItem>
                  <MenuItem value="HT40">HT40</MenuItem>
                  <MenuItem value="HT80">HT80</MenuItem>
                  <MenuItem value="HT160">HT160</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                label="TX Power Up"
                type="number"
                fullWidth
                value={addTreeDraft.tx_power_up}
                onChange={(e) => setAddTreeDraft(d => ({ ...d, tx_power_up: e.target.value }))}
              />
              <TextField
                size="small"
                label="TX Power Down"
                type="number"
                fullWidth
                value={addTreeDraft.tx_power_down}
                onChange={(e) => setAddTreeDraft(d => ({ ...d, tx_power_down: e.target.value }))}
              />
              <TextField
                size="small"
                label="Max Children"
                type="number"
                fullWidth
                value={addTreeDraft.max_children}
                onChange={(e) => setAddTreeDraft(d => ({ ...d, max_children: e.target.value }))}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTreeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddTreeSubmit}
            disabled={!addTreeDraft.tree_id}
          >
            Add Tree
          </Button>
        </DialogActions>
      </Dialog>

      {/* Parent Selection Dialog */}
      <Dialog open={parentDialogOpen} onClose={handleCloseParentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Set Parent Device</DialogTitle>
        <DialogContent>
          {parentUpdateStatus.message && (
            <Alert severity={parentUpdateStatus.type} sx={{ mb: 2 }}>
              {parentUpdateStatus.message}
            </Alert>
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Parent Device</InputLabel>
            <Select
              value={selectedParent}
              onChange={(e) => setSelectedParent(e.target.value)}
              label="Select Parent Device"
              MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 320 } } }}
            >
              {renderAccessListSelectFilter(parentDeviceFilter, setParentDeviceFilter)}
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {filteredParentDevices.map((dev) => (
                <MenuItem key={dev.serial_number} value={dev.serial_number}>
                  {dev.name || dev.serial_number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseParentDialog}>Cancel</Button>
          <Button 
            onClick={handleSetParent} 
            variant="contained" 
            color="primary"
            disabled={selectedParent.length === 0}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Access Lists Management Dialog */}
      <Dialog open={accessListDialogOpen} onClose={handleCloseAccessListDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Manage Access Lists
        </DialogTitle>
        <DialogContent>
          {accessListUpdateStatus.message && (
            <Alert severity={accessListUpdateStatus.type} sx={{ mb: 2 }}>
              {accessListUpdateStatus.message}
            </Alert>
          )}
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Whitelist insist time (seconds)"
                type="number"
                fullWidth
                size="small"
                value={accessListInsistSec}
                onChange={(e) => setAccessListInsistSec(e.target.value)}
                inputProps={{ min: 1, max: 600, step: 1 }}
                helperText="Optional. 1–600 seconds; firmware hold time for the whitelist. Leave empty to keep the current value. Sent before access-list changes."
              />
            </Grid>
            {/* Whitelist Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.main', mb: 2 }}>
                Whitelist (Allowed Devices)
              </Typography>
              <FormControl fullWidth size="medium">
                <InputLabel id="whitelist-label">Select Whitelisted Devices</InputLabel>
                <Select
                  labelId="whitelist-label"
                  multiple
                  value={selectedWhitelist}
                  onChange={handleWhitelistChange}
                  label="Select Whitelisted Devices"
                  MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 320 } } }}
                  sx={{
                    minHeight: '56px',
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      padding: '8px 14px',
                    }
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                      {selected.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          No devices selected
                        </Typography>
                      ) : (
                        selected.map((value) => {
                          const device = availableDevices.find(d => d.serial_number === value);
                          return (
                            <Chip
                              key={value}
                              label={device?.name || value}
                              color="success"
                              size="small"
                              onDelete={() => {
                                setSelectedWhitelist(prev => prev.filter(id => id !== value));
                              }}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                            />
                          );
                        })
                      )}
                    </Box>
                  )}
                >
                  {renderAccessListSelectFilter(whitelistDeviceFilter, setWhitelistDeviceFilter)}
                  {filteredWhitelistDevices.map((device) => (
                    <MenuItem key={device.serial_number} value={device.serial_number}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: device.connected ? '#4caf50' : '#f44336',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" noWrap>
                          {device.name || device.serial_number}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Blacklist Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: 'error.main', mb: 2 }}>
                Blacklist (Blocked Devices)
              </Typography>
              <FormControl fullWidth size="medium">
                <InputLabel id="blacklist-label">Select Blacklisted Devices</InputLabel>
                <Select
                  labelId="blacklist-label"
                  multiple
                  value={selectedBlacklist}
                  onChange={handleBlacklistChange}
                  label="Select Blacklisted Devices"
                  MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 320 } } }}
                  sx={{
                    minHeight: '56px',
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      padding: '8px 14px',
                    }
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                      {selected.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          No devices selected
                        </Typography>
                      ) : (
                        selected.map((value) => {
                          const device = availableDevices.find(d => d.serial_number === value);
                          return (
                            <Chip
                              key={value}
                              label={device?.name || value}
                              color="error"
                              size="small"
                              onDelete={() => {
                                setSelectedBlacklist(prev => prev.filter(id => id !== value));
                              }}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                            />
                          );
                        })
                      )}
                    </Box>
                  )}
                >
                  {renderAccessListSelectFilter(blacklistDeviceFilter, setBlacklistDeviceFilter)}
                  {filteredBlacklistDevices.map((device) => (
                    <MenuItem key={device.serial_number} value={device.serial_number}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: device.connected ? '#4caf50' : '#f44336',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" noWrap>
                          {device.name || device.serial_number}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚠️ Important: Settings are applied immediately but may take up to 2 minutes to refresh in the interface.
            </Typography>
          </Box>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
            <Typography variant="body2" color="info.main">
              <strong>Note:</strong> Whitelist allows only the selected devices to connect to this device's AP. 
              Blacklist prevents the selected devices from connecting. If a device is in both lists, 
              the blacklist takes precedence.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAccessListDialog}>Cancel</Button>
          <Button 
            onClick={handleSetAccessList} 
            variant="contained" 
            color="primary"
          >
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Command Dialog */}
      <Dialog open={commandDialogOpen} onClose={handleCloseCommandDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Send Command to Device</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Command"
            type="text"
            fullWidth
            variant="outlined"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            multiline
            rows={4}
          />
          {commandStatus.message && (
            <Alert severity={commandStatus.type} sx={{ mt: 2 }}>
              {commandStatus.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCommandDialog}>Cancel</Button>
          <Button 
            onClick={handleSendCommand} 
            variant="contained" 
            color="primary"
            disabled={!command.trim()}
          >
            Send Command
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      </Box>
    </Box>
  );
};

export default DeviceDetails; 